import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { FiasBridgeContext } from './context';
import { FiasBridgeError, getBridge } from './bridge';
import { ENTITY_INVOCATION_REQUEST_LIMITS } from './invocation-params';
import type {
  ArcheHandoffPayload,
  FiasUser,
  FiasTheme,
  FiasStorageApi,
  DataSubscriptionState,
  EntityInvocationApi,
  EntityInvocationOptions,
  EntityInvocationParams,
  EntityInvocationResult,
  FiasTextModel,
  ClientEntityApi,
  ImageGenerationApi,
  ImageGenerationParams,
  ImageGenerationResult,
  ImageEntityFilter,
  ImageEntitySummary,
  NllbLanguageOption,
  AudioGenerationApi,
  AudioGenerationParams,
  AudioGenerationResult,
  FiasNavigationApi,
  ArcheHandoffPayloadInput,
  StepNavigationApi,
  StepNavigationOptions,
  FiasDataStoreApi,
  DataStoreCollection,
  DataStoreWritePolicy,
  DataStoreCollectionConstraints,
  DataStoreBatchOptions,
  DataStoreBatchResult,
  DataStorePutResult,
  DataStoreDocument,
  DataStoreQueryOptions,
  DataStoreQueryResult,
  DataStoreSearchOptions,
  DataStoreSearchMatch,
  DataStoreScopeOptions,
  DataStoreBatchOp,
  FiasWorkspacesApi,
  DataStoreWorkspace,
  DataStoreWorkspaceMember,
  WorkspaceRole,
  FiasStoreApi,
  StoreProduct,
  StoreEntitlement,
  StorePurchase,
  StorePurchaseResult,
  StorePurchaseOptions,
  VaultDocumentsApi,
  VaultDocumentsListOptions,
  VaultDocumentsListResult,
  VaultDocumentDownloadUrl,
  VaultDocumentWriteParams,
  VaultDocumentUpdateParams,
  VaultDocumentSaveContentParams,
  VaultDocumentSaveContentResult,
  VaultUserDocumentSaveContentResult,
  VaultDocumentSearchOptions,
  VaultDocumentSearchResult,
  VaultDocumentReferenceTargetType,
  VaultDocumentReferenceResult,
  VaultDocumentSaveCopyOptions,
  VaultDocumentUploadParams,
  VaultDocumentUploadInitResult,
  VaultDocumentUploadResult,
  ArcheAssetsApi,
  FiasContentApi,
  FiasContentErrorCode,
  FiasContentFile,
  FiasContentUrl,
  ArcheAssetsListOptions,
  ArcheAssetsListResult,
  ArcheAssetsIndexOptions,
  ArcheAssetsIndexResult,
  ArcheAsset,
  CommunityAsset,
  CommunityAssetUrl,
  CommunityAssetsApi,
  CommunityAssetPublishInput,
  CommunityAssetPublishResult,
  CommunityAssetListOptions,
  CommunityAssetListResult,
  CommunityAssetReportReason,
  CompanyEmailApi,
  CompanyEmailSendParams,
  CompanyEmailSendResult,
  FiasAIActionsDeclaration,
} from './types';
import type { FiasBridge } from './bridge';
import {
  invokeEntityOp,
  invokeStoreOp,
  PLUGIN_STORAGE_ENTITY_ID,
  PLUGIN_DATASTORE_ENTITY_ID,
  PLUGIN_WORKSPACES_ENTITY_ID,
  VAULT_USER_DOCUMENTS_ENTITY_ID,
  DOCUMENT_SIGN_ENTITY_ID,
  VAULT_ARCHE_DOCUMENTS_ENTITY_ID,
  COMMUNITY_ASSETS_ENTITY_ID,
  COMPANY_EMAIL_SEND_ENTITY_ID,
  COMMUNITY_ASSET_MAX_URL_BATCH,
  COMMUNITY_ASSET_MAX_PUBLISH_BATCH,
} from './entity-ops';

/**
 * Internal helper to get bridge from context, throws if not wrapped in FiasProvider.
 */
function useBridge(): FiasBridge {
  const bridge = useContext(FiasBridgeContext);
  if (!bridge) {
    throw new Error('useBridge must be used within a <FiasProvider>');
  }
  return bridge;
}

/**
 * Trailing-edge debounce window for persistent-state writes. Rapid setter
 * calls (e.g. per-frame updates from a requestAnimationFrame game loop) are
 * coalesced so we don't trip the bridge's 50-in-5s runaway-loop detector or
 * exhaust the 120/minute storage_write budget.
 */
const PERSIST_DEBOUNCE_WAIT_MS = 250;
const PERSIST_DEBOUNCE_MAX_WAIT_MS = 1000;

interface PendingWrite {
  path: string;
  content: string;
  timerId: ReturnType<typeof setTimeout> | null;
  firstScheduledAt: number;
}

interface DebouncedWriter {
  schedule(path: string, content: string): void;
  flush(): void;
}

/**
 * Create a debounced storage_write scheduler. Writes are coalesced to the
 * last value seen within a 250ms idle window, capped at a 1s max-wait so
 * continuous updates still persist at least once per second. Call `flush()`
 * on unmount or before switching keys to avoid losing the pending value.
 *
 * If `schedule` is called with a different `path` than the currently pending
 * write, the pending write flushes synchronously before the new one is
 * queued — each path gets a write.
 */
function createDebouncedWriter(bridge: FiasBridge): DebouncedWriter {
  let pending: PendingWrite | null = null;

  function flush(): void {
    if (!pending) return;
    const { path, content, timerId } = pending;
    if (timerId !== null) clearTimeout(timerId);
    pending = null;
    invokeEntityOp<void>(bridge, PLUGIN_STORAGE_ENTITY_ID, 'write', { path, content }).catch(
      () => {},
    );
  }

  function schedule(path: string, content: string): void {
    const now = Date.now();
    if (pending && pending.path !== path) {
      flush();
    }
    if (!pending) {
      pending = { path, content, timerId: null, firstScheduledAt: now };
    } else {
      pending.content = content;
    }
    if (pending.timerId !== null) clearTimeout(pending.timerId);
    const elapsed = now - pending.firstScheduledAt;
    const remainingMax = Math.max(0, PERSIST_DEBOUNCE_MAX_WAIT_MS - elapsed);
    const delay = Math.min(PERSIST_DEBOUNCE_WAIT_MS, remainingMax);
    pending.timerId = setTimeout(flush, delay);
  }

  return { schedule, flush };
}

/**
 * Access the current user's profile information.
 * Requires the `user:profile:read` permission in fias-plugin.json.
 *
 * If the `get_user` bridge request fails — most commonly because the plugin
 * called this hook without declaring `user:profile:read` — the error is
 * thrown during render so it surfaces to the nearest error boundary
 * (`FiasProvider` installs one). Previously a failed/denied request left the
 * hook permanently returning `null`, indistinguishable from "still loading",
 * so a UI that gated on `if (!user)` hung on an infinite spinner forever.
 *
 * @returns The current user's profile or null while loading.
 */
export function useFiasUser(): FiasUser | null {
  const bridge = useBridge();
  const [user, setUser] = useState<FiasUser | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;
    bridge.request<FiasUser>('get_user', {}).then(
      (data) => {
        if (mounted) setUser(data);
      },
      (err: unknown) => {
        if (mounted) setError(err instanceof Error ? err : new Error(String(err)));
      },
    );
    return () => {
      mounted = false;
    };
  }, [bridge]);

  if (error) throw error;

  return user;
}

/**
 * Access platform theme tokens for consistent styling.
 * Automatically updates when the platform theme changes (e.g., light/dark mode).
 * Requires the `theme:read` permission in fias-plugin.json.
 *
 * @returns The current theme tokens or null while loading.
 */
export function useFiasTheme(): FiasTheme | null {
  const bridge = useBridge();
  const [theme, setTheme] = useState<FiasTheme | null>(bridge.getTheme());

  useEffect(() => {
    return bridge.onThemeUpdate(setTheme);
  }, [bridge]);

  return theme;
}

/**
 * Self-hosted asset URLs for a platform-vendored library, for plugins that
 * declare the `sandbox:vendored-libraries` permission. Returns a file-key → URL
 * map (for `pdfjs-dist`: `{ mainModule, worker, cMapUrl, standardFontDataUrl }`),
 * or `null` when the host's CDN is unconfigured or the library isn't vendored
 * (the plugin should then degrade gracefully).
 *
 * Delivered in the init payload (no gated request); values are static for the
 * session. The import specifier is already pinned to its main module via the
 * host importmap, so a plugin typically only needs `worker` (pass to the
 * pdf-core loader's `workerModuleUrl`) and the CMap/font directories.
 *
 * @param importSpecifier The vendored library's bare module name (e.g. `'pdfjs-dist'`).
 * @returns The library's asset URLs, or null if unavailable.
 */
export function useFiasVendoredAssets(importSpecifier: string): Record<string, string> | null {
  const bridge = useBridge();
  return bridge.getVendoredAssets(importSpecifier);
}

/**
 * Access the plugin's sandboxed storage.
 * Requires the `storage:sandbox` permission in fias-plugin.json.
 *
 * @returns Storage API methods (readFile, writeFile, listFiles, deleteFile).
 */
export function useFiasStorage(): FiasStorageApi {
  const bridge = useBridge();

  const readFile = useCallback(
    async (path: string): Promise<string | null> => {
      const res = await invokeEntityOp<{ content: string | null; exists: boolean }>(
        bridge,
        PLUGIN_STORAGE_ENTITY_ID,
        'read',
        { path },
      );
      return res?.content ?? null;
    },
    [bridge],
  );

  const writeFile = useCallback(
    async (path: string, content: string): Promise<void> => {
      await invokeEntityOp<{ success: boolean }>(bridge, PLUGIN_STORAGE_ENTITY_ID, 'write', {
        path,
        content,
      });
    },
    [bridge],
  );

  const listFiles = useCallback(
    async (prefix?: string): Promise<string[]> => {
      const res = await invokeEntityOp<{ files: string[] }>(
        bridge,
        PLUGIN_STORAGE_ENTITY_ID,
        'list',
        { prefix },
      );
      return res?.files ?? [];
    },
    [bridge],
  );

  const deleteFile = useCallback(
    async (path: string): Promise<void> => {
      await invokeEntityOp<{ success: boolean }>(bridge, PLUGIN_STORAGE_ENTITY_ID, 'delete', {
        path,
      });
    },
    [bridge],
  );

  // Memoized, like useArcheAssets. An unmemoized object literal here is a
  // footgun rather than a style question: the natural way to use this hook is
  // `useEffect(() => { void storage.readFile(...) }, [storage])`, and a fresh
  // identity every render turns that into an infinite loop — effect, setState,
  // render, effect. The inner callbacks were already stable; only the
  // container was not.
  return useMemo(
    () => ({ readFile, writeFile, listFiles, deleteFile }),
    [readFile, writeFile, listFiles, deleteFile],
  );
}

/**
 * Invoke platform entities (AI models, etc.) through the bridge.
 * Requires the `entities:invoke` permission in fias-plugin.json.
 *
 * Rendering pattern: `streamingText` accumulates tokens during the call AND
 * keeps the full text after `isLoading` flips to `false` — it is not cleared
 * when the response completes. `result.output` also holds the full text once
 * the promise resolves. **Render only one slot** (e.g.
 * `{isLoading ? streamingText : result?.output}`) — rendering both prints the
 * response twice once the stream completes.
 *
 * Concurrent calls: pass `onToken` (and optionally `onThinking`) as the second
 * argument to `invoke`. Those callbacks are scoped to that one call, and a call
 * that supplies `onToken` leaves the shared `streamingText` / `result` / `error`
 * alone (its failure arrives as the rejected promise),
 * so several streams can render side by side. `isLoading` is true while ANY
 * call is in flight.
 *
 * @returns Entity invocation API (invoke, isLoading, result, error, streamingText).
 */
export function useEntityInvocation(): EntityInvocationApi {
  const bridge = useBridge();
  const [inFlight, setInFlight] = useState(0);
  const [result, setResult] = useState<EntityInvocationResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [streamingText, setStreamingText] = useState('');

  const invoke = useCallback(
    async (
      params: EntityInvocationParams,
      options?: EntityInvocationOptions,
    ): Promise<EntityInvocationResult> => {
      // A call with its own onToken owns its output; the shared slots stay
      // with calls that rely on them, so concurrent streams never interleave.
      const usesSharedSlots = !options?.onToken;
      setInFlight((n) => n + 1);
      if (usesSharedSlots) {
        setError(null);
        setStreamingText('');
      }

      try {
        const res = await bridge.requestStream(params, {
          onToken: (token) => {
            if (options?.onToken) {
              options.onToken(token);
              return;
            }
            // flushSync forces React to render each token immediately.
            // Without it, React batches rapid stream_token messages into
            // a single render, making all text appear at once.
            flushSync(() => {
              setStreamingText((prev) => prev + token);
            });
          },
          onThinking: options?.onThinking,
          signal: options?.signal,
        });
        if (usesSharedSlots) setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        // A call with its own callbacks gets its failure from the rejection;
        // stopping one of several concurrent streams must not flag the rest.
        if (usesSharedSlots) setError(e);
        throw e;
      } finally {
        setInFlight((n) => n - 1);
      }
    },
    [bridge],
  );

  return { invoke, isLoading: inFlight > 0, result, error, streamingText };
}

/**
 * The platform's live catalog of text models this plugin can invoke — for a
 * model picker. Each entry's `entityId` goes straight into
 * `useEntityInvocation().invoke({ entityId, ... })`; `capabilities` says which
 * models accept images and `parameters.reasoning`; `pricing` is what the user
 * pays (credits per 1M tokens, markup included) for a cost label.
 *
 * Requires the `entities:invoke` permission. Returns `[]` until the host has
 * delivered the catalog (usually immediately), then re-renders with it.
 * Retired models drop out, so a saved model choice may disappear: fall back to
 * a `recommended` entry when it does.
 */
export function useTextModels(): FiasTextModel[] {
  const bridge = useBridge();
  const [models, setModels] = useState<FiasTextModel[]>(bridge.getTextModels() ?? []);

  useEffect(() => {
    // The catalog may have arrived between the initial render and this effect.
    setModels(bridge.getTextModels() ?? []);
    return bridge.onTextModelsUpdate(setModels);
  }, [bridge]);

  return models;
}

/**
 * How long `generate` waits for an image. High-quality renders at large sizes
 * run past two minutes, and the platform keeps working on them for up to
 * 300 s. This is the outermost deadline — above the host's (310 s) — so a
 * slow image is delivered and a stuck one is reported by the platform with a
 * real reason, never cut off here after it was already charged.
 */
const IMAGE_GENERATE_TIMEOUT_MS = 320_000;

/**
 * Generate images using platform AI image models.
 *
 * Images are saved to the user's Fias file system and a presigned URL
 * is returned for display. Most images take 10-60 seconds; high quality at
 * large sizes can take several minutes, so the call waits up to
 * `IMAGE_GENERATE_TIMEOUT_MS`.
 *
 * @deprecated Phase 3 of the Invocation Surface Pattern migrates image
 * generation onto the registry. Prefer the generated typed hook
 * (`useImageGenerate()` from `@fias/arche-sdk` once the image surface
 * registers) over this hand-written one. The legacy `image_generate`
 * bridge op continues to work through the deprecation window. Tracking:
 * docs/architecture/invocation-surface-pattern.md §7 Phase 3.
 *
 * @returns Image generation API (generate, isLoading, result, error).
 */
export function useImageGeneration(): ImageGenerationApi {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ImageGenerationResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const generate = useCallback(
    async (params: ImageGenerationParams): Promise<ImageGenerationResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await bridge.request<ImageGenerationResult>(
          'image_generate',
          params,
          IMAGE_GENERATE_TIMEOUT_MS,
        );
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge],
  );

  return { generate, isLoading, result, error };
}

/**
 * Discover image-generation entities by capability.
 *
 * Use when the plugin wants to render a picker (e.g., "let the user choose
 * which model to use"). For the common case of "just give me something
 * good," pass a selector directly to `useImageGeneration` instead —
 * `{ entityId: { platformRecommended: { mode: 'text-to-image' } }, ... }`
 * — and skip the discovery round-trip entirely.
 *
 * The filter is shallow-compared by JSON serialization. If you build a new
 * filter object on every render (`useImageEntities({ modes: ['text-to-image'] })`),
 * the equality check still avoids refetches — but pass a stable reference if
 * you can.
 *
 * @param filter Capability filter. Omit to list every published image entity.
 *
 * @example
 *   const { entities, isLoading } = useImageEntities({
 *     modes: ['image-to-image'],
 *     supportsReferenceImage: true,
 *   });
 */
export function useImageEntities(filter?: ImageEntityFilter): {
  entities: ImageEntitySummary[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const bridge = useBridge();
  const [entities, setEntities] = useState<ImageEntitySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Serialize the filter so changing-but-equivalent object refs don't
  // trigger refetches.
  const filterKey = useMemo(() => JSON.stringify(filter ?? {}), [filter]);

  const fetchOnce = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await bridge.request<{ entities: ImageEntitySummary[] }>(
        'entities_list_image',
        { filter: JSON.parse(filterKey) as ImageEntityFilter },
        30_000,
      );
      setEntities(res.entities ?? []);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [bridge, filterKey]);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  return { entities, isLoading, error, refetch: fetchOnce };
}

/**
 * `useBackgroundRemoval()` was REMOVED. It targeted the on-device imgly
 * background-removal entity, which was retired platform-wide for
 * licensing reasons (AGPL-3.0), along with its
 * `entities:image_remove_background` permission.
 *
 * There is no client-side replacement. Server-side background removal is
 * available to plugins through the paid `image.smart-remove-background`
 * invocation surface (Stability / Recraft) under the
 * `entities:image_edit` permission.
 */

/**
 * Default timeout for a client-side entity invocation. Client-side entities
 * (WASM models, dictionary engines) can take many seconds to warm up and
 * run, so we use the same 2-minute ceiling as the image/audio surfaces.
 */
const CLIENT_ENTITY_INVOKE_TIMEOUT_MS = 120_000;

/**
 * Invoke a client-side ("on-device") entity through the bridge's generic
 * `client_entity_invoke` op. Client-side entities execute entirely in the
 * browser (e.g. the nspell + retext grammar checker, the NLLB-200
 * translator) — input never leaves the device and there is no per-call
 * credit cost.
 *
 * This is the client-side counterpart to {@link useEntityInvocation}, which
 * invokes server-side, credit-billed AI entities. Target an entity by its ID
 * and supply the input shape that entity documents; the typed result flows
 * back through the returned promise:
 *
 * ```tsx
 * const { invoke, isLoading, error } = useClientEntity();
 * const { issues } = await invoke<GrammarCheckInput, GrammarCheckResult>(
 *   GRAMMAR_CHECK_ENTITY_ID,
 *   { text },
 * );
 * ```
 *
 * Requires the `entities:client_invoke` permission in `fias-plugin.json`.
 * Per-entity rate limits (each entity's `pluginInvocable.rateLimitPerMinute`)
 * are enforced by the host.
 *
 * Input limits are enforced **host-side by each entity's executor**, not by
 * this hook — being generic, it cannot know a given entity's caps (e.g. the
 * grammar entity's 100,000-character limit). Oversized/invalid input is
 * rejected after it crosses the bridge, surfacing as a rejected promise /
 * `error`. If you want to fail fast before the round-trip, pre-validate in
 * your own code against the limits the entity documents.
 */
export function useClientEntity(): ClientEntityApi {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const invoke = useCallback(
    async <TInput = unknown, TResult = unknown>(
      entityId: string,
      input: TInput,
    ): Promise<TResult> => {
      setIsLoading(true);
      setError(null);

      try {
        return await bridge.request<TResult>(
          'client_entity_invoke',
          { entityId, input },
          CLIENT_ENTITY_INVOKE_TIMEOUT_MS,
        );
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge],
  );

  return { invoke, isLoading, error };
}

/**
 * Entity ID of the NLLB-200 on-device translation entity. Exported so plugins
 * can target it via the generic {@link useClientEntity} hook, with
 * `TranslateParams` / `TranslationResult` as the I/O types.
 *
 * SYNC NOTE: must match `NLLB_TRANSLATION_ENTITY_ID` in
 * `packages/entities/src/integrations/nllb-translation/config.ts`. The
 * architecture test `sdk-entity-id-constants-match.test.ts` pins this —
 * drift fails CI.
 */
export const NLLB_TRANSLATION_ENTITY_ID = 'ent_intg_nllb_xlate_000000000000';

/**
 * Curated list of target languages for the NLLB translator, as
 * label/FLORES-200-code pairs. Not exhaustive (NLLB supports 200 languages) —
 * a convenient default set for building a picker. Sorted by label.
 */
export const NLLB_LANGUAGES: readonly NllbLanguageOption[] = [
  { label: 'Arabic', code: 'arb_Arab' },
  { label: 'Bengali', code: 'ben_Beng' },
  { label: 'Bulgarian', code: 'bul_Cyrl' },
  { label: 'Catalan', code: 'cat_Latn' },
  { label: 'Chinese (Simplified)', code: 'zho_Hans' },
  { label: 'Chinese (Traditional)', code: 'zho_Hant' },
  { label: 'Croatian', code: 'hrv_Latn' },
  { label: 'Czech', code: 'ces_Latn' },
  { label: 'Danish', code: 'dan_Latn' },
  { label: 'Dutch', code: 'nld_Latn' },
  { label: 'English', code: 'eng_Latn' },
  { label: 'Finnish', code: 'fin_Latn' },
  { label: 'French', code: 'fra_Latn' },
  { label: 'German', code: 'deu_Latn' },
  { label: 'Greek', code: 'ell_Grek' },
  { label: 'Hebrew', code: 'heb_Hebr' },
  { label: 'Hindi', code: 'hin_Deva' },
  { label: 'Hungarian', code: 'hun_Latn' },
  { label: 'Indonesian', code: 'ind_Latn' },
  { label: 'Italian', code: 'ita_Latn' },
  { label: 'Japanese', code: 'jpn_Jpan' },
  { label: 'Korean', code: 'kor_Hang' },
  { label: 'Norwegian', code: 'nob_Latn' },
  { label: 'Persian', code: 'pes_Arab' },
  { label: 'Polish', code: 'pol_Latn' },
  { label: 'Portuguese', code: 'por_Latn' },
  { label: 'Romanian', code: 'ron_Latn' },
  { label: 'Russian', code: 'rus_Cyrl' },
  { label: 'Serbian', code: 'srp_Cyrl' },
  { label: 'Slovak', code: 'slk_Latn' },
  { label: 'Spanish', code: 'spa_Latn' },
  { label: 'Swedish', code: 'swe_Latn' },
  { label: 'Tamil', code: 'tam_Taml' },
  { label: 'Telugu', code: 'tel_Telu' },
  { label: 'Thai', code: 'tha_Thai' },
  { label: 'Turkish', code: 'tur_Latn' },
  { label: 'Ukrainian', code: 'ukr_Cyrl' },
  { label: 'Urdu', code: 'urd_Arab' },
  { label: 'Vietnamese', code: 'vie_Latn' },
];

/**
 * Entity ID of the grammar_check client-side entity. Exported so plugins can
 * target it via the generic {@link useClientEntity} hook. Literal here so the
 * SDK stays bundle-clean (no `@fias/entities` runtime dep).
 *
 * SYNC NOTE: must match `GRAMMAR_CHECK_ENTITY_ID` in
 * `packages/entities/src/integrations/grammar-check/config.ts`. The
 * architecture test `sdk-entity-id-constants-match.test.ts` pins this —
 * drift fails CI.
 */
export const GRAMMAR_CHECK_ENTITY_ID = 'ent_intg_grammar_check_000000000';

/**
 * Generate audio (music today; future speech/SFX sub-types via the same
 * hook) via the bridge's `audio_generate` op. Returns an array of clips
 * with same-origin signed proxy URLs suitable for `<audio src>` playback
 * inside the plugin iframe.
 *
 * Lyria 4×30 s clips can take ~90–120 s end-to-end, so the per-request
 * timeout is 3 minutes (vs image's 2 minutes).
 *
 * @deprecated Phase 2 of the Invocation Surface Pattern shipped typed
 * per-surface hooks: `useLyriaMusic()` (Lyria), `useElevenLabsTts()`,
 * `useElevenLabsSfx()`, `useElevenLabsMusic()`, etc. They invoke through
 * the `surface_invoke` bridge op with per-surface Zod-validated params.
 * The legacy `audio_generate` op continues to work through the
 * deprecation window. Tracking: docs/architecture/invocation-surface-pattern.md
 * §7 Phase 3.
 *
 * @returns Audio generation API (generate, isLoading, result, error).
 */
export function useAudioGeneration(): AudioGenerationApi {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AudioGenerationResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const generate = useCallback(
    async (params: AudioGenerationParams): Promise<AudioGenerationResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await bridge.request<AudioGenerationResult>('audio_generate', params, 180_000);
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge],
  );

  return { generate, isLoading, result, error };
}

/**
 * Generic Invocation Surface hook.
 *
 * Plugin code should usually call one of the **generated typed hooks**
 * (e.g. `useElevenLabsTts()`) from `./generated/surfaces` rather than this
 * directly — the typed hooks bake in the `surfaceKey` and infer the param
 * and result types from the surface's Zod schemas.
 *
 * This hook is the hand-written substrate they delegate to. Direct use is
 * fine when invoking a surface by dynamic key (rare).
 *
 * @param surfaceKey - Stable kebab-case key registered with the platform.
 * @param timeoutMs  - Per-request timeout. Defaults to 180s to cover the
 *                     longest-running synchronous surfaces (TTS large texts,
 *                     Lyria multi-clip generations).
 */
export function useSurface<TParams, TResult>(surfaceKey: string, timeoutMs = 180_000) {
  const bridge = useBridge();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const invoke = useCallback(
    async (params: TParams): Promise<TResult> => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await bridge.request<TResult>(
          'surface_invoke',
          { surfaceKey, params },
          timeoutMs,
        );
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [bridge, surfaceKey, timeoutMs],
  );

  return { invoke, isLoading, result, error };
}

/**
 * Navigate within the plugin's route space (`navigateTo`), or open another
 * arche's page (`openArche`, requires the `navigation:open_arche` permission).
 *
 * @returns Navigation API (navigateTo, openArche, currentPath).
 */
export function useFiasNavigation(): FiasNavigationApi {
  const bridge = useBridge();
  const [currentPath, setCurrentPath] = useState(bridge.getCurrentPath());

  useEffect(() => {
    // Covers the "subscribed after init" race — if init already arrived
    // before this effect ran, the init-time listener broadcast missed us,
    // and our initial useState value may or may not be stale depending on
    // whether the render captured pre- or post-init state. Syncing on
    // ready() is cheap and deterministic.
    let cancelled = false;
    bridge.waitForInit().then(() => {
      if (!cancelled) setCurrentPath(bridge.getCurrentPath());
    });
    const unsubscribe = bridge.onNavigationUpdate(setCurrentPath);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [bridge]);

  const navigateTo = useCallback(
    (path: string, opts?: { replace?: boolean }) => {
      // `replace` is spread in only when true, so a plain navigation keeps the
      // exact `{ path }` wire shape shipped plugins already emit.
      bridge.request<void>('navigate', { path, ...(opts?.replace ? { replace: true } : {}) });
    },
    [bridge],
  );

  const openArche = useCallback(
    (
      archeId: string,
      opts?: { newTab?: boolean; path?: string; payload?: ArcheHandoffPayloadInput },
    ) => {
      // `path` / `payload` are spread in only when supplied: an older host
      // ignores unknown keys, but sending `path: undefined` would add a key to
      // every call for no reason and churn the wire shape shipped plugins
      // already emit.
      bridge.request<void>('open_arche', {
        archeId,
        newTab: opts?.newTab === true,
        ...(opts?.path ? { path: opts.path } : {}),
        ...(opts?.payload ? { payload: opts.payload } : {}),
      });
    },
    [bridge],
  );

  // Memoized for the same reason as useFiasStorage. `currentPath` is state, so
  // this identity still changes when the path does — which is correct, and is
  // exactly what a consumer depending on it wants.
  return useMemo(
    () => ({ navigateTo, openArche, currentPath }),
    [navigateTo, openArche, currentPath],
  );
}

/**
 * Like useState, but auto-persists to bridge storage.
 * Survives preview rebuilds during development.
 *
 * @param key - Unique storage key for this piece of state
 * @param initialValue - Default value if nothing is stored
 */
export function usePersistentState<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const bridge = useBridge();
  const [value, setValueInternal] = useState<T>(initialValue);
  const initializedRef = useRef(false);
  const valueRef = useRef<T>(initialValue);
  // Set once the plugin calls the setter. A value set BEFORE the mount read
  // resolves is newer than anything in storage, so the read must not
  // overwrite it — it used to, silently reverting e.g. a click that landed
  // while the read was in flight (the debounced write then persisted the
  // user's value while the UI showed the stale one).
  const setByPluginRef = useRef(false);
  const writer = useMemo(() => createDebouncedWriter(bridge), [bridge]);

  // Load from storage on mount; flush any pending write on unmount/key change
  // so rapid updates in a game loop never lose their last value.
  useEffect(() => {
    invokeEntityOp<{
      content: string | null;
      exists: boolean;
    }>(bridge, PLUGIN_STORAGE_ENTITY_ID, 'read', { path: `__state/${key}` })
      .then((result) => {
        if (setByPluginRef.current) {
          initializedRef.current = true;
          return;
        }
        if (result.exists && result.content !== null) {
          try {
            const parsed = JSON.parse(result.content) as T;
            valueRef.current = parsed;
            setValueInternal(parsed);
          } catch {
            // Corrupted data — use initial value
          }
        }
        initializedRef.current = true;
      })
      .catch(() => {
        // A failed/denied storage_read (e.g. the plugin didn't declare
        // `storage:sandbox`) must not become a silent unhandled rejection.
        // Persistence is best-effort — fall back to the initial value and
        // let subsequent writes fail on their own rather than hanging here.
        initializedRef.current = true;
      });
    return () => {
      writer.flush();
    };
  }, [bridge, key, writer]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(valueRef.current) : next;
      setByPluginRef.current = true;
      valueRef.current = resolved;
      setValueInternal(resolved);
      writer.schedule(`__state/${key}`, JSON.stringify(resolved));
    },
    [key, writer],
  );

  return [value, setValue];
}

/**
 * Manage step navigation for multi-node plugins.
 * Listens for step_navigate messages from the host to support
 * external navigation (e.g., clicking graph nodes).
 *
 * Pass `{ persistKey }` to make the current step survive preview rebuilds.
 * This is the correct way to persist step state — do NOT additionally wrap
 * currentStep in `usePersistentState`, which creates two independent state
 * sources and a bidirectional sync loop.
 *
 * @param initialStep - Optional initial step/node ID.
 * @param options - Optional config; set `persistKey` to enable persistence.
 * @returns Step navigation API (currentStep, setCurrentStep).
 */
export function useStepNavigation(
  initialStep?: string,
  options?: StepNavigationOptions,
): StepNavigationApi {
  const bridge = useBridge();
  const persistKey = options?.persistKey;
  const [currentStep, setCurrentStepInternal] = useState<string | null>(initialStep ?? null);
  const initializedRef = useRef(false);
  const writer = useMemo(() => createDebouncedWriter(bridge), [bridge]);

  // Load persisted value on mount when persistKey is provided; flush any
  // pending write on unmount/key change so we don't lose the last nav.
  useEffect(() => {
    if (!persistKey) {
      initializedRef.current = true;
      return;
    }
    let cancelled = false;
    invokeEntityOp<{ content: string | null; exists: boolean }>(
      bridge,
      PLUGIN_STORAGE_ENTITY_ID,
      'read',
      { path: `__state/${persistKey}` },
    )
      .then((result) => {
        // Don't clobber a user-initiated setCurrentStep that landed before this resolved.
        if (cancelled || initializedRef.current) return;
        if (result.exists && result.content !== null) {
          try {
            const parsed = JSON.parse(result.content);
            if (typeof parsed === 'string' || parsed === null) {
              setCurrentStepInternal(parsed);
            }
          } catch {
            // Corrupted data — keep initial value
          }
        }
        initializedRef.current = true;
      })
      .catch(() => {
        initializedRef.current = true;
      });
    return () => {
      cancelled = true;
      writer.flush();
    };
  }, [bridge, persistKey, writer]);

  // Subscribe to host-driven step navigation.
  useEffect(() => {
    return bridge.onStepNavigationUpdate((nodeId) => {
      setCurrentStepInternal(nodeId);
      if (persistKey) {
        writer.schedule(`__state/${persistKey}`, JSON.stringify(nodeId));
      }
    });
  }, [bridge, persistKey, writer]);

  const setCurrentStep = useCallback(
    (nodeId: string | null) => {
      initializedRef.current = true;
      setCurrentStepInternal(nodeId);
      if (persistKey) {
        writer.schedule(`__state/${persistKey}`, JSON.stringify(nodeId));
      }
    },
    [persistKey, writer],
  );

  return { currentStep, setCurrentStep };
}

/**
 * Access the Arche Data Store for structured, queryable data persistence.
 * Requires the `data:store` permission in fias-plugin.json.
 *
 * Collections can be 'user'-scoped (each user sees only their own data)
 * or 'shared' (all users of the arche share data).
 *
 * @example
 * const dataStore = useFiasDataStore();
 *
 * // Create a collection (once)
 * await dataStore.createCollection('scores', { userScope: 'user' });
 *
 * // Store a document
 * await dataStore.put('scores', 'level-1', { score: 100, stars: 3 });
 *
 * // Get a document
 * const doc = await dataStore.get<{ score: number }>('scores', 'level-1');
 *
 * // Query with filters
 * const results = await dataStore.query('scores', {
 *   filters: [{ field: 'score', op: 'gte', value: 50 }],
 *   orderBy: { field: 'score', direction: 'desc' },
 *   limit: 10,
 * });
 */
export function useFiasDataStore(): FiasDataStoreApi {
  const bridge = useBridge();

  const createCollection = useCallback(
    async (
      name: string,
      options?: {
        userScope?: 'user' | 'shared' | 'workspace';
        searchable?: { field: string };
        writeMinRole?: WorkspaceRole;
        readMinRole?: WorkspaceRole;
        writePolicy?: DataStoreWritePolicy;
        constraints?: DataStoreCollectionConstraints;
      },
    ): Promise<DataStoreCollection> => {
      const res = await invokeEntityOp<{ collection: DataStoreCollection }>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'create_collection',
        {
          name,
          userScope: options?.userScope ?? 'user',
          searchable: options?.searchable,
          writeMinRole: options?.writeMinRole,
          readMinRole: options?.readMinRole,
          writePolicy: options?.writePolicy,
          constraints: options?.constraints,
        },
      );
      return res.collection;
    },
    [bridge],
  );

  const listCollections = useCallback(async (): Promise<DataStoreCollection[]> => {
    const res = await invokeEntityOp<{ collections: DataStoreCollection[] }>(
      bridge,
      PLUGIN_DATASTORE_ENTITY_ID,
      'list_collections',
    );
    return res.collections;
  }, [bridge]);

  const deleteCollection = useCallback(
    async (name: string): Promise<void> => {
      await invokeEntityOp(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'delete_collection', { name });
    },
    [bridge],
  );

  const put = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      key: string,
      data: T,
      options?: DataStoreScopeOptions,
    ): Promise<DataStorePutResult> => {
      const res = await invokeEntityOp<{ version?: number }>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'put',
        {
          collection,
          key,
          data,
          workspaceId: options?.workspaceId,
        },
      );
      return { version: res?.version };
    },
    [bridge],
  );

  const get = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      key: string,
      options?: DataStoreScopeOptions,
    ): Promise<T | null> => {
      const res = await invokeEntityOp<{ data: T | null }>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'get',
        {
          collection,
          key,
          workspaceId: options?.workspaceId,
        },
      );
      return res.data;
    },
    [bridge],
  );

  const getDocument = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      key: string,
      options?: DataStoreScopeOptions,
    ): Promise<DataStoreDocument<T> | null> => {
      const res = await invokeEntityOp<{
        data: T | null;
        key?: string;
        updatedAt?: string;
        version?: number;
        authoredByCollaborator?: boolean;
      }>(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'get', {
        collection,
        key,
        workspaceId: options?.workspaceId,
      });
      if (res.data === null || res.data === undefined) return null;
      return {
        key: res.key ?? key,
        data: res.data,
        updatedAt: res.updatedAt ?? '',
        ...(res.version !== undefined ? { version: res.version } : {}),
        ...(res.authoredByCollaborator !== undefined
          ? { authoredByCollaborator: res.authoredByCollaborator }
          : {}),
      };
    },
    [bridge],
  );

  const query = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      options?: DataStoreQueryOptions,
      scope?: DataStoreScopeOptions,
    ): Promise<DataStoreQueryResult<T>> => {
      return invokeEntityOp<DataStoreQueryResult<T>>(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'query', {
        collection,
        ...options,
        // `query`/`search` are the only doc methods that split options from
        // scope, so `{ workspaceId }` lands in the options slot by mistake —
        // and overwriting unconditionally turned that into a SILENT scope
        // loss: the payload went out with no workspaceId and the collection
        // answered WORKSPACE_REQUIRED, pointing the author at the collection
        // rather than at the call. Take the scope arg when it carries one and
        // otherwise leave whatever the spread put there.
        ...(scope?.workspaceId !== undefined ? { workspaceId: scope.workspaceId } : {}),
      });
    },
    [bridge],
  );

  const search = useCallback(
    async <T extends Record<string, unknown> = Record<string, unknown>>(
      collection: string,
      queryText: string,
      options?: DataStoreSearchOptions,
      scope?: DataStoreScopeOptions,
    ): Promise<DataStoreSearchMatch<T>[]> => {
      const res = await invokeEntityOp<{ matches: DataStoreSearchMatch<T>[] }>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'search',
        {
          collection,
          query: queryText,
          ...options,
          // See `query` above — never blank a caller-supplied workspaceId.
          ...(scope?.workspaceId !== undefined ? { workspaceId: scope.workspaceId } : {}),
        },
      );
      return res.matches;
    },
    [bridge],
  );

  const deleteDoc = useCallback(
    async (collection: string, key: string, options?: DataStoreScopeOptions): Promise<void> => {
      await invokeEntityOp(bridge, PLUGIN_DATASTORE_ENTITY_ID, 'delete', {
        collection,
        key,
        workspaceId: options?.workspaceId,
      });
    },
    [bridge],
  );

  const batch = useCallback(
    async (
      operations: DataStoreBatchOp[],
      options?: DataStoreBatchOptions,
    ): Promise<DataStoreBatchResult> => {
      const res = await invokeEntityOp<DataStoreBatchResult>(
        bridge,
        PLUGIN_DATASTORE_ENTITY_ID,
        'batch',
        {
          operations,
          ...(options?.preconditions?.length ? { preconditions: options.preconditions } : {}),
        },
      );
      return { written: res?.written };
    },
    [bridge],
  );

  return useMemo(
    () => ({
      createCollection,
      listCollections,
      deleteCollection,
      put,
      get,
      getDocument,
      query,
      search,
      delete: deleteDoc,
      batch,
    }),
    [
      createCollection,
      listCollections,
      deleteCollection,
      put,
      get,
      getDocument,
      query,
      search,
      deleteDoc,
      batch,
    ],
  );
}

/**
 * Access workspace tenancy — team/org workspaces with role-gated membership
 * (owner/admin/member/viewer) over `workspace`-scoped Data Store collections
 * (ADR-010). The creating user becomes the founding owner; a workspace always
 * keeps at least one owner.
 *
 * Requires the `data:workspace` permission in fias-plugin.json. To read/write a
 * workspace's documents, use `useFiasDataStore()` with `{ workspaceId }` on a
 * collection created with `userScope: 'workspace'`.
 *
 * @example
 * const workspaces = useFiasWorkspaces();
 * const ws = await workspaces.create('Acme Corp');
 * await workspaces.addMember(ws.workspaceId, otherUserId, 'member');
 * // then, with useFiasDataStore():
 * await dataStore.put('ledger', 'tx-1', { amount: 100 }, { workspaceId: ws.workspaceId });
 *
 * @returns Workspace API with lifecycle + membership methods.
 */
export function useFiasWorkspaces(): FiasWorkspacesApi {
  const bridge = useBridge();

  const create = useCallback(
    async (displayName: string): Promise<DataStoreWorkspace> => {
      const res = await invokeEntityOp<{ workspace: DataStoreWorkspace }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'create',
        { displayName },
      );
      return res.workspace;
    },
    [bridge],
  );

  const list = useCallback(async (): Promise<DataStoreWorkspace[]> => {
    const res = await invokeEntityOp<{ workspaces: DataStoreWorkspace[] }>(
      bridge,
      PLUGIN_WORKSPACES_ENTITY_ID,
      'list',
    );
    return res.workspaces;
  }, [bridge]);

  const get = useCallback(
    async (workspaceId: string): Promise<DataStoreWorkspace> => {
      const res = await invokeEntityOp<{ workspace: DataStoreWorkspace }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'get',
        { workspaceId },
      );
      return res.workspace;
    },
    [bridge],
  );

  const archive = useCallback(
    async (workspaceId: string): Promise<void> => {
      await invokeEntityOp(bridge, PLUGIN_WORKSPACES_ENTITY_ID, 'archive', { workspaceId });
    },
    [bridge],
  );

  const listMembers = useCallback(
    async (workspaceId: string): Promise<DataStoreWorkspaceMember[]> => {
      const res = await invokeEntityOp<{ members: DataStoreWorkspaceMember[] }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'list_members',
        { workspaceId },
      );
      return res.members;
    },
    [bridge],
  );

  const addMember = useCallback(
    async (
      workspaceId: string,
      username: string,
      role: WorkspaceRole,
      expiresAt?: string,
    ): Promise<DataStoreWorkspaceMember> => {
      const res = await invokeEntityOp<{ member: DataStoreWorkspaceMember }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'add_member',
        {
          workspaceId,
          username,
          role,
          expiresAt,
        },
      );
      return res.member;
    },
    [bridge],
  );

  const updateMember = useCallback(
    async (
      workspaceId: string,
      username: string,
      role: WorkspaceRole,
    ): Promise<DataStoreWorkspaceMember> => {
      const res = await invokeEntityOp<{ member: DataStoreWorkspaceMember }>(
        bridge,
        PLUGIN_WORKSPACES_ENTITY_ID,
        'update_member',
        { workspaceId, username, role },
      );
      return res.member;
    },
    [bridge],
  );

  const removeMember = useCallback(
    async (workspaceId: string, username: string): Promise<void> => {
      await invokeEntityOp(bridge, PLUGIN_WORKSPACES_ENTITY_ID, 'remove_member', {
        workspaceId,
        username,
      });
    },
    [bridge],
  );

  return useMemo(
    () => ({ create, list, get, archive, listMembers, addMember, updateMember, removeMember }),
    [create, list, get, archive, listMembers, addMember, updateMember, removeMember],
  );
}

/**
 * Access the in-arche purchase (IAP) store.
 * Provides product listing, purchase initiation, entitlement checking,
 * and purchase history. All purchases are mediated by the platform.
 *
 * Requires the `store:purchase` permission in fias-plugin.json.
 *
 * @returns Store API with products, entitlements, and purchase methods.
 */
export function useFiasStore(): FiasStoreApi {
  const bridge = useBridge();
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [entitlements, setEntitlements] = useState<StoreEntitlement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load products and entitlements on mount
  useEffect(() => {
    let mounted = true;

    Promise.all([
      invokeStoreOp<StoreProduct[]>(bridge, 'get_products'),
      invokeStoreOp<StoreEntitlement[]>(bridge, 'get_entitlements'),
    ])
      .then(([prods, ents]) => {
        if (mounted) {
          setProducts(prods);
          setEntitlements(ents);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [bridge]);

  const purchase = useCallback(
    async (
      productIdentifier: string,
      options?: StorePurchaseOptions,
    ): Promise<StorePurchaseResult> => {
      const result = await invokeStoreOp<StorePurchaseResult>(bridge, 'purchase', {
        productIdentifier,
        ...options,
      });

      // If purchase succeeded and returned an entitlement, update local state
      if (result.success && result.entitlement) {
        setEntitlements((prev) => {
          const existing = prev.findIndex(
            (e) => e.productIdentifier === result.entitlement!.productIdentifier,
          );
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = result.entitlement!;
            return updated;
          }
          return [...prev, result.entitlement!];
        });
      }

      return result;
    },
    [bridge],
  );

  const hasEntitlement = useCallback(
    (productIdentifier: string): boolean => {
      return entitlements.some((e) => e.productIdentifier === productIdentifier && e.isActive);
    },
    [entitlements],
  );

  const getPurchaseHistory = useCallback(async (): Promise<StorePurchase[]> => {
    const res = await invokeStoreOp<{ purchases: StorePurchase[] }>(bridge, 'get_purchase_history');
    return res.purchases;
  }, [bridge]);

  const restorePurchases = useCallback(async (): Promise<StoreEntitlement[]> => {
    const restored = await invokeStoreOp<StoreEntitlement[]>(bridge, 'restore');
    setEntitlements(restored);
    return restored;
  }, [bridge]);

  return useMemo(
    () => ({
      products,
      purchase,
      entitlements,
      hasEntitlement,
      getPurchaseHistory,
      restorePurchases,
      isLoading,
    }),
    [
      products,
      purchase,
      entitlements,
      hasEntitlement,
      getPurchaseHistory,
      restorePurchases,
      isLoading,
    ],
  );
}

/**
 * Refresh a presigned download URL this many milliseconds before it actually
 * expires. Catches the case where a render starts with a still-valid URL but
 * the image load completes after the URL has expired.
 */
const DOWNLOAD_URL_REFRESH_BUFFER_MS = 60_000;

interface DownloadUrlCacheEntry {
  url: string;
  expiresAt: number;
  contentType: string | null;
}

/**
 * Process-lifetime cache of presigned download URLs, keyed by documentId.
 * Shared across all useVaultDocuments() consumers in the plugin so 50
 * thumbnails on a page don't each trigger a separate bridge call. Inflight
 * requests are also deduped so a burst of mounts coalesces to one network
 * roundtrip per documentId.
 *
 * The cache is intentionally module-scoped (not React state): React renders
 * are not the right invalidation boundary — presigned URLs are valid for a
 * fixed wall-clock window regardless of component lifecycle.
 */
const downloadUrlCache = new Map<string, DownloadUrlCacheEntry>();
const downloadUrlInflight = new Map<string, Promise<VaultDocumentDownloadUrl>>();

/**
 * Cache-aware fetcher for a presigned download URL. Used internally by
 * `useVaultDocuments().getDownloadUrl()`. Exposed separately so non-React
 * code paths (e.g. PDF export, image preloaders) can hit the same cache
 * without having to plumb a React context through.
 */
export async function fetchVaultDocumentDownloadUrl(
  documentId: string,
): Promise<VaultDocumentDownloadUrl> {
  const now = Date.now();
  const cached = downloadUrlCache.get(documentId);
  if (cached && now < cached.expiresAt - DOWNLOAD_URL_REFRESH_BUFFER_MS) {
    return { url: cached.url, expiresAt: cached.expiresAt, contentType: cached.contentType };
  }
  const inflight = downloadUrlInflight.get(documentId);
  if (inflight) return inflight;

  const bridge = getBridge();
  // The server bridge returns `expiresAt` as an ISO 8601 string; the
  // SDK surface uses epoch milliseconds. Normalize here so callers
  // don't have to think about the wire shape.
  const promise = bridge
    .request<{ url: string; expiresAt: string; contentType: string | null }>(
      'vault_documents_download_url',
      { documentId },
    )
    .then((result) => {
      const normalized: VaultDocumentDownloadUrl = {
        url: result.url,
        expiresAt: new Date(result.expiresAt).getTime(),
        contentType: result.contentType,
      };
      downloadUrlCache.set(documentId, {
        url: normalized.url,
        expiresAt: normalized.expiresAt,
        contentType: normalized.contentType,
      });
      return normalized;
    })
    .finally(() => {
      downloadUrlInflight.delete(documentId);
    });
  downloadUrlInflight.set(documentId, promise);
  return promise;
}

/**
 * Access the user's Vault Documents, scoped to documents this arche owns.
 *
 * The most common use is `getDownloadUrl(documentId)` for images created
 * via `useImageGeneration()` — those calls return a `documentId`, and this
 * hook lets the plugin refresh the presigned URL after the original ~1h
 * TTL expires (so backgrounds/thumbnails persist across sessions).
 *
 * Permissions:
 *   - `vault:documents:read` for list / read / getDownloadUrl
 *   - `vault:documents:write` for write / update / delete
 *
 * @returns Vault Documents API (list, read, getDownloadUrl, write, update,
 *          delete).
 */
export function useVaultDocuments(): VaultDocumentsApi {
  const bridge = useBridge();

  const list = useCallback(
    async (options?: VaultDocumentsListOptions): Promise<VaultDocumentsListResult> => {
      return bridge.request<VaultDocumentsListResult>('vault_documents_list', {
        search: options?.search,
        limit: options?.limit,
        cursor: options?.cursor,
      });
    },
    [bridge],
  );

  const read = useCallback(
    async (
      documentId: string,
    ): Promise<{ content: string; contentType: string; revision?: string }> => {
      return bridge.request<{ content: string; contentType: string; revision?: string }>(
        'vault_documents_read',
        { documentId },
      );
    },
    [bridge],
  );

  const getDownloadUrl = useCallback(
    (documentId: string): Promise<VaultDocumentDownloadUrl> =>
      fetchVaultDocumentDownloadUrl(documentId),
    // The hook closes over the React context's bridge, but the resolver
    // uses the singleton — they are the same instance in practice. Keeping
    // `bridge` in the deps for symmetry with other hooks that do go through
    // the context-bound bridge.
    [bridge],
  );

  const readBytes = useCallback(
    async (
      documentId: string,
    ): Promise<{
      document: {
        documentId: string;
        name: string;
        mimeType: string | null;
        sizeBytes: number | null;
        sensitivity: string;
        createdAt: string;
        updatedAt: string;
        revision?: string;
      };
      bytes: Uint8Array;
      contentType: string | null;
    }> => {
      const res = await invokeEntityOp<{
        document: {
          documentId: string;
          name: string;
          mimeType: string | null;
          sizeBytes: number | null;
          sensitivity: string;
          createdAt: string;
          updatedAt: string;
          revision?: string;
        };
        bytesBase64: string;
        contentType: string | null;
      }>(bridge, VAULT_ARCHE_DOCUMENTS_ENTITY_ID, 'get_own_document_bytes', { documentId });
      return {
        document: res.document,
        bytes: base64ToBytes(res.bytesBase64),
        contentType: res.contentType,
      };
    },
    [bridge],
  );

  const saveContent = useCallback(
    async (params: VaultDocumentSaveContentParams): Promise<VaultDocumentSaveContentResult> => {
      const routed = await routeSaveContent(params.content);
      let res: VaultDocumentSaveContentResult;
      if (routed.lane === 'body') {
        // Small text rides the entity_invoke body.
        res = await invokeEntityOp<VaultDocumentSaveContentResult>(
          bridge,
          VAULT_ARCHE_DOCUMENTS_ENTITY_ID,
          'save_own_document_content',
          {
            documentId: params.documentId,
            content: routed.text,
            contentType: params.contentType,
            expectedRevision: params.expectedRevision,
          },
        );
      } else {
        // Bytes — or text too large for the bridge body — go through the
        // host, which does the presigned PUT the iframe's CSP forbids (same
        // reason `upload` does; see there). `contentKind: 'text'` keeps the
        // text lane's must-be-a-text-type rule in force at any size.
        //
        // Deliberately NO `name` in this payload: a host that predates
        // in-place saves requires one for an upload, so it refuses this
        // message instead of silently creating a NEW document.
        res = await bridge.request<VaultDocumentSaveContentResult>('vault_documents_upload', {
          saveToDocumentId: params.documentId,
          bytes: bytesToBase64(routed.bytes),
          mimeType: params.contentType,
          contentKind: routed.contentKind,
          expectedRevision: params.expectedRevision,
        });
      }
      // The bytes behind any cached download URL just changed.
      downloadUrlCache.delete(params.documentId);
      return res;
    },
    [bridge],
  );

  const write = useCallback(
    async (params: VaultDocumentWriteParams): Promise<{ documentId: string }> => {
      // `write` has no second lane to fall back to, so say so here rather
      // than let the request die as an opaque 413 at the bridge.
      if (
        params.content.length > SAVE_CONTENT_BODY_LANE_MAX_BYTES ||
        !fitsBridgeBodyAsJsonText(params.content)
      ) {
        throw new FiasBridgeError(
          'write() takes small text only: at most 200,000 characters, and small enough to ' +
            'travel in one bridge request once JSON-escaped (quotes, backslashes and newlines ' +
            'double). Use upload() for larger documents.',
          'INVALID_PARAMS',
        );
      }
      return bridge.request<{ documentId: string }>('vault_documents_write', {
        name: params.name,
        content: params.content,
        contentType: params.contentType,
        tags: params.tags,
        sensitivity: params.sensitivity,
        folderPath: params.folderPath,
        replacesDocumentId: params.replacesDocumentId,
      });
    },
    [bridge],
  );

  const update = useCallback(
    async (documentId: string, params: VaultDocumentUpdateParams): Promise<void> => {
      await bridge.request('vault_documents_update', {
        documentId,
        name: params.name,
        tags: params.tags,
      });
      // Rename rewrites the Content-Disposition the URL would carry, so
      // drop any cached URL for this document.
      downloadUrlCache.delete(documentId);
    },
    [bridge],
  );

  const deleteDocument = useCallback(
    async (documentId: string): Promise<void> => {
      await bridge.request('vault_documents_delete', { documentId });
      downloadUrlCache.delete(documentId);
      downloadUrlInflight.delete(documentId);
    },
    [bridge],
  );

  const search = useCallback(
    async (
      query: string,
      options?: VaultDocumentSearchOptions,
    ): Promise<VaultDocumentSearchResult> => {
      return bridge.request<VaultDocumentSearchResult>('vault_documents_search', {
        query,
        topK: options?.topK,
        minSimilarity: options?.minSimilarity,
        includeContent: options?.includeContent,
      });
    },
    [bridge],
  );

  const attach = useCallback(
    async (
      documentId: string,
      target: { regardingType: VaultDocumentReferenceTargetType; regardingId: string },
    ): Promise<VaultDocumentReferenceResult> => {
      return bridge.request<VaultDocumentReferenceResult>('vault_documents_attach', {
        documentId,
        regardingType: target.regardingType,
        regardingId: target.regardingId,
      });
    },
    [bridge],
  );

  const detach = useCallback(
    async (documentId: string, referenceId: string): Promise<void> => {
      await bridge.request('vault_documents_detach', { documentId, referenceId });
    },
    [bridge],
  );

  const uploadInit = useCallback(
    async (
      params: Omit<VaultDocumentUploadParams, 'userVisible' | 'destination'> & {
        sizeBytes: number;
        checksumSha256: string;
      },
    ): Promise<VaultDocumentUploadInitResult> => {
      // No destination here by construction: a save into the user's
      // Documents needs the host's Save dialog, which it can only show when it
      // orchestrates the whole upload. The host REJECTS a `visibility` here.
      return bridge.request<VaultDocumentUploadInitResult>('vault_documents_upload_init', {
        name: params.name,
        mimeType: params.mimeType,
        sizeBytes: params.sizeBytes,
        checksumSha256: params.checksumSha256,
        tags: params.tags,
        sensitivity: params.sensitivity,
        folderPath: params.folderPath,
        replacesDocumentId: params.replacesDocumentId,
      });
    },
    [bridge],
  );

  const uploadFinalize = useCallback(
    async (params: {
      documentId: string;
      checksumSha256: string;
    }): Promise<VaultDocumentUploadResult> => {
      return bridge.request<VaultDocumentUploadResult>('vault_documents_upload_finalize', {
        documentId: params.documentId,
        checksumSha256: params.checksumSha256,
      });
    },
    [bridge],
  );

  const upload = useCallback(
    async (
      bytes: ArrayBuffer | Uint8Array,
      params: VaultDocumentUploadParams,
    ): Promise<VaultDocumentUploadResult> => {
      // The presigned PUT must run in the HOST, not here: the plugin iframe's
      // CSP is `connect-src 'none'`, which silently blocks an in-iframe
      // `fetch` PUT in published plugins. The host (parent window, no
      // connect-src restriction) orchestrates init → PUT → finalize via the
      // `vault_documents_upload` bridge op; the presigned URL is backend-issued
      // host-side, so the iframe can't redirect the PUT (no SSRF). The
      // lower-level `uploadInit`/`uploadFinalize` remain for advanced callers.
      const src = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
      return bridge.request<VaultDocumentUploadResult>('vault_documents_upload', {
        name: params.name,
        mimeType: params.mimeType,
        bytes: bytesToBase64(src),
        tags: params.tags,
        sensitivity: params.sensitivity,
        folderPath: params.folderPath,
        replacesDocumentId: params.replacesDocumentId,
        // A save into the user's Documents. The host turns this into its Save
        // dialog, which the iframe cannot see or script, and performs the
        // write itself from the user's session. Omitted (the common case)
        // keeps the arche-private default.
        destination: params.destination,
        userVisible: params.userVisible,
      });
    },
    [bridge],
  );

  const saveCopy = useCallback(
    async (
      documentId: string,
      options?: VaultDocumentSaveCopyOptions,
    ): Promise<VaultDocumentUploadResult> => {
      // Rides the update op, intercepted by the host: it reads the bytes and
      // shows the Save dialog. Never forwarded — the original is unchanged.
      return bridge.request<VaultDocumentUploadResult>('vault_documents_update', {
        documentId,
        saveCopy: { ...(options?.suggestedName ? { suggestedName: options.suggestedName } : {}) },
      });
    },
    [bridge],
  );

  return useMemo(
    () => ({
      list,
      read,
      readBytes,
      saveContent,
      getDownloadUrl,
      write,
      update,
      delete: deleteDocument,
      search,
      attach,
      detach,
      upload,
      saveCopy,
      uploadInit,
      uploadFinalize,
    }),
    [
      list,
      read,
      readBytes,
      saveContent,
      getDownloadUrl,
      write,
      update,
      deleteDocument,
      search,
      attach,
      detach,
      upload,
      saveCopy,
      uploadInit,
      uploadFinalize,
    ],
  );
}

/** Encoded-size ceiling of the text lane (`save_own_document_content`). Must
 *  match the server's MAX_OWN_DOCUMENT_SAVE_BYTES; content past it is routed
 *  through the host-uploaded lane rather than refused. */
const SAVE_CONTENT_BODY_LANE_MAX_BYTES = 200_000;

/**
 * What the text may occupy ON THE WIRE. The bridge request body is capped at
 * 256 KB (the server's `PLUGIN_BRIDGE_BODY_LIMIT_BYTES`) and the text travels
 * inside it as a JSON string, where every `"`, `\` and newline doubles and
 * every other control character becomes six bytes. So 200 KB of text can be
 * 400 KB — or 1.2 MB — of request, and the answer is an opaque 413 rather
 * than a save. Routing therefore looks at the SERIALIZED size too, with ~16 KB
 * left for the envelope (ids, revision, content type, JSON keys).
 */
const BRIDGE_BODY_TEXT_MAX_SERIALIZED_BYTES =
  ENTITY_INVOCATION_REQUEST_LIMITS.textBytes - 16 * 1024;

/** True when `text` fits a bridge request body as a JSON string. Encodes at
 *  most once, and not at all for short strings (a UTF-16 unit is at most six
 *  bytes of JSON). */
function fitsBridgeBodyAsJsonText(text: string, utf8Bytes?: number): boolean {
  if (text.length * 6 <= BRIDGE_BODY_TEXT_MAX_SERIALIZED_BYTES) return true;
  // JSON escapes are ASCII, so they cost the same in UTF-16 units as in
  // bytes: serialized bytes = UTF-8 bytes + the units escaping added.
  const escapeOverhead = JSON.stringify(text).length - text.length;
  const bytes = utf8Bytes ?? new TextEncoder().encode(text).length;
  return bytes + escapeOverhead <= BRIDGE_BODY_TEXT_MAX_SERIALIZED_BYTES;
}

/** Mirrors the server's `MAX_OWN_DOCUMENT_PRESIGNED_SAVE_BYTES` — the most an
 *  in-place save accepts, pinned to what `readBytes` / `getBytes` can serve
 *  back, so anything saved can be reopened. */
const SAVE_CONTENT_MAX_BYTES = 15 * 1024 * 1024;

/** `saveContent`'s content, routed: small text rides the bridge body as-is;
 *  everything else is bytes for the host to upload. */
type SaveContentRouting =
  | { lane: 'body'; text: string }
  | { lane: 'host'; bytes: Uint8Array; contentKind: 'text' | 'binary' };

/**
 * Pick the lane for `saveContent` from the content itself, encoding text AT
 * MOST ONCE (a UTF-16 code unit is at most 3 UTF-8 bytes, so short strings
 * are routed without encoding at all) and refusing anything past the cap
 * BEFORE the base64 step — which would otherwise build a 20 MB string on the
 * plugin's main thread for a save the server is certain to refuse.
 */
async function routeSaveContent(
  content: string | Uint8Array | ArrayBuffer | Blob,
): Promise<SaveContentRouting> {
  let routed: SaveContentRouting;
  if (typeof content === 'string') {
    // Short enough that neither limit can be reached, whatever it contains:
    // six bytes of JSON per unit is the worst case, three of UTF-8.
    if (content.length * 6 <= SAVE_CONTENT_BODY_LANE_MAX_BYTES) {
      return { lane: 'body', text: content };
    }
    const bytes = new TextEncoder().encode(content);
    routed =
      bytes.length <= SAVE_CONTENT_BODY_LANE_MAX_BYTES &&
      fitsBridgeBodyAsJsonText(content, bytes.length)
        ? { lane: 'body', text: content }
        : { lane: 'host', bytes, contentKind: 'text' };
  } else {
    routed = { lane: 'host', bytes: await toUint8Array(content), contentKind: 'binary' };
  }
  if (routed.lane === 'host' && routed.bytes.length > SAVE_CONTENT_MAX_BYTES) {
    throw new FiasBridgeError(
      `Content is ${routed.bytes.length} bytes; saveContent accepts at most ` +
        `${SAVE_CONTENT_MAX_BYTES} bytes (15 MB).`,
      'OWN_DOCUMENT_SAVE_TOO_LARGE',
    );
  }
  return routed;
}

async function toUint8Array(content: Uint8Array | ArrayBuffer | Blob): Promise<Uint8Array> {
  if (content instanceof Uint8Array) return content;
  if (content instanceof ArrayBuffer) return new Uint8Array(content);
  return new Uint8Array(await content.arrayBuffer());
}

/** Decode base64 transport back to bytes (`get_consented_document_bytes`). */
function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Base64-encode bytes for transport to the host's `vault_documents_upload`
 *  op (the host does the presigned PUT — see PluginBridge.handleVaultUpload). */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Access the contributor-curated asset library for this arche.
 *
 * In a PUBLISHED arche the library is a frozen snapshot taken at publish
 * time; uploads the contributor makes afterwards go live with the next
 * publish, which re-snapshots from their current library.
 *
 * In the ARCHE BUILDER PREVIEW it reads the contributor's current
 * uploads directly — i.e. exactly what that next publish would
 * snapshot — so an image looks the same before and after shipping.
 * Only `active` assets appear; a soft-deleted one is already gone.
 *
 * Either way the library can legitimately be empty (nothing uploaded
 * yet), so handle that rather than assuming a match.
 *
 * Each asset has an immutable `assetId`; cache them in your datastore /
 * state, and refresh `signedUrl` via `getUrl(assetId)` when it's about
 * to expire.
 *
 * Permission: `assets:read`.
 *
 * @example
 *   const { list, getUrl } = useArcheAssets();
 *   const { entries } = await list({ limit: 50 });
 *   // entries[i].signedUrl is a short-lived URL you can use as <img src>
 */
export function useArcheAssets(): ArcheAssetsApi {
  const bridge = useBridge();

  const list = useCallback(
    async (options?: ArcheAssetsListOptions): Promise<ArcheAssetsListResult> => {
      return bridge.request<ArcheAssetsListResult>('arche_assets_list', {
        cursor: options?.cursor,
        limit: options?.limit,
        tags: options?.tags,
      });
    },
    [bridge],
  );

  const index = useCallback(
    async (options?: ArcheAssetsIndexOptions): Promise<ArcheAssetsIndexResult> => {
      return bridge.request<ArcheAssetsIndexResult>('arche_assets_index', {
        tags: options?.tags,
        groupBy: options?.groupBy,
      });
    },
    [bridge],
  );

  const getUrl = useCallback(
    async (assetId: string): Promise<ArcheAsset> => {
      return bridge.request<ArcheAsset>('arche_assets_get_url', { assetId });
    },
    [bridge],
  );

  return useMemo(() => ({ list, index, getUrl }), [list, index, getUrl]);
}

/**
 * Entity ID of the plugin content pack client-side entity (ADR-021) — the
 * host-side reader behind {@link useFiasContent}. Literal here so the SDK
 * stays bundle-clean (no `@fias/entities` runtime dep).
 *
 * SYNC NOTE: must match `PLUGIN_CONTENT_ENTITY_ID` in
 * `packages/entities/src/integrations/plugin-content/config.ts`. The
 * architecture test `sdk-entity-id-constants-match.test.ts` pins this.
 */
export const PLUGIN_CONTENT_ENTITY_ID = 'ent_intg_plugin_content_00000000';

/** A failed content read: `code` says why, `path` names the file when there is one. */
export class FiasContentError extends Error {
  constructor(
    message: string,
    readonly code: FiasContentErrorCode,
    readonly path?: string,
  ) {
    super(message);
    this.name = 'FiasContentError';
  }
}

type ContentFileResult =
  | { text: string; mimeType: string }
  | { bytes: ArrayBuffer; mimeType: string }
  | { url: string; expiresAt: string }
  | { error: { code: FiasContentErrorCode; message: string } };

interface ContentResponse {
  files?: FiasContentFile[];
  results?: Record<string, ContentFileResult>;
  error?: { code: FiasContentErrorCode; message: string };
}

function toContentError(err: unknown): FiasContentError {
  if (err instanceof FiasContentError) return err;
  const message = err instanceof Error ? err.message : String(err);
  const code = (err as { code?: unknown } | null)?.code;
  if (/permission denied/i.test(message)) return new FiasContentError(message, 'PERMISSION_DENIED');
  if (code === 'RATE_LIMITED' || /rate limit/i.test(message)) {
    return new FiasContentError(message, 'RATE_LIMIT');
  }
  return new FiasContentError(message, 'CONTENT_UNAVAILABLE');
}

function fileResult(
  results: Record<string, ContentFileResult> | undefined,
  path: string,
): ContentFileResult {
  const result = results?.[path];
  if (!result) {
    throw new FiasContentError(`No result for content/${path}`, 'CONTENT_UNAVAILABLE', path);
  }
  if ('error' in result) throw new FiasContentError(result.error.message, result.error.code, path);
  return result;
}

/**
 * `blob:` URLs built in this iframe, per path. A published pack is immutable,
 * so one URL per path serves the plugin for its whole life.
 */
const contentObjectUrls = new Map<string, Promise<string>>();

/**
 * Read this plugin's own content pack — the reviewed `content/` directory it
 * was published with (lessons, data, images, audio, fonts).
 *
 * The host page reads the pack from the Fias content CDN, verifies every file
 * against its hash, and caches it; nothing here touches the network, and a
 * plugin can only read its own pack. Reads are free for your users; the
 * contributor pays the pack's storage and bandwidth, and cached repeat reads
 * cost nothing.
 *
 * Paths are relative to `content/`. In the Arche Builder and developer
 * previews there is no published pack: calls fail with
 * `CONTENT_UNAVAILABLE_IN_PREVIEW` (the dev harness serves your local
 * `content/` instead).
 *
 * Permission: `entities:client_invoke`.
 *
 * @example
 *   const content = useFiasContent();
 *   const lessons = await content.list('lessons/');
 *   const markdown = await content.getText(lessons[0].path);
 *   const cover = await content.getObjectUrl('img/cover.webp'); // <img src={cover}>
 */
export function useFiasContent(): FiasContentApi {
  const bridge = useBridge();

  const call = useCallback(
    async (input: Record<string, unknown>): Promise<ContentResponse> => {
      let response: ContentResponse;
      try {
        response = await bridge.request<ContentResponse>(
          'client_entity_invoke',
          { entityId: PLUGIN_CONTENT_ENTITY_ID, input },
          CLIENT_ENTITY_INVOKE_TIMEOUT_MS,
        );
      } catch (err) {
        throw toContentError(err);
      }
      if (response?.error) {
        throw new FiasContentError(response.error.message, response.error.code);
      }
      return response ?? {};
    },
    [bridge],
  );

  const list = useCallback(
    async (prefix?: string): Promise<FiasContentFile[]> =>
      (await call({ verb: 'list', ...(prefix !== undefined ? { prefix } : {}) })).files ?? [],
    [call],
  );

  const getText = useCallback(
    async (path: string): Promise<string> => {
      const { results } = await call({ verb: 'read', paths: [path], as: 'text' });
      return (fileResult(results, path) as { text: string }).text;
    },
    [call],
  );

  const getJson = useCallback(
    async <T = unknown>(path: string): Promise<T> => JSON.parse(await getText(path)) as T,
    [getText],
  );

  const readBytes = useCallback(
    async (path: string): Promise<{ bytes: ArrayBuffer; mimeType: string }> => {
      const { results } = await call({ verb: 'read', paths: [path], as: 'bytes' });
      return fileResult(results, path) as { bytes: ArrayBuffer; mimeType: string };
    },
    [call],
  );

  const getBytes = useCallback(
    async (path: string): Promise<ArrayBuffer> => (await readBytes(path)).bytes,
    [readBytes],
  );

  const getMany = useCallback(
    async (paths: string[], as: 'text' | 'bytes') => {
      const { results } = await call({ verb: 'read', paths, as });
      const out: Record<string, string | ArrayBuffer | Error> = {};
      for (const path of paths) {
        try {
          const result = fileResult(results, path);
          out[path] =
            as === 'text'
              ? (result as { text: string }).text
              : (result as { bytes: ArrayBuffer }).bytes;
        } catch (err) {
          out[path] = toContentError(err);
        }
      }
      return out;
    },
    [call],
  ) as FiasContentApi['getMany'];

  const getObjectUrl = useCallback(
    async (path: string): Promise<string> => {
      const cached = contentObjectUrls.get(path);
      if (cached) return cached;
      const pending = readBytes(path).then(({ bytes, mimeType }) =>
        URL.createObjectURL(new Blob([bytes], { type: mimeType })),
      );
      contentObjectUrls.set(path, pending);
      pending.catch(() => contentObjectUrls.delete(path));
      return pending;
    },
    [readBytes],
  );

  const getUrl = useCallback(
    async (path: string): Promise<FiasContentUrl> => {
      const { results } = await call({ verb: 'url', paths: [path] });
      const { url, expiresAt } = fileResult(results, path) as FiasContentUrl;
      return { url, expiresAt };
    },
    [call],
  );

  return useMemo(
    () => ({ list, getText, getJson, getBytes, getMany, getObjectUrl, getUrl }),
    [list, getText, getJson, getBytes, getMany, getObjectUrl, getUrl],
  );
}

/**
 * Preserve transient UI state across a builder-preview rebuild.
 *
 * In the arche builder, editing code (or a background cross-device sync)
 * rebuilds the preview, which reloads the iframe and wipes the plugin's
 * in-memory state — losing, for example, a report the user just generated.
 * This hook lets you opt that state into host-side preservation: just before
 * the reload the host snapshots `getState()`, and after the reload it hands the
 * value back to `onRestore`.
 *
 * Inert outside the builder preview (the host never requests a capture), so
 * it's safe to leave in production plugin code.
 *
 * @param key       Stable namespace for this slice of state (so multiple calls
 *                  don't collide). Use a literal like `'report'`.
 * @param getState  Returns the current serializable state. Must be JSON-safe.
 * @param onRestore Receives the snapshot after a reload. Reads the latest
 *                  closure via a ref, so you don't need a stable identity.
 *
 * @example
 * const [report, setReport] = useState<Report | null>(null);
 * useFiasPreviewState('report', () => report, (saved) => setReport(saved as Report));
 */
export function useFiasPreviewState<T>(
  key: string,
  getState: () => T,
  onRestore: (state: T) => void,
): void {
  const bridge = useBridge();
  const getStateRef = useRef(getState);
  getStateRef.current = getState;
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  useEffect(() => {
    return bridge.registerPreviewState(
      key,
      () => getStateRef.current(),
      (state) => onRestoreRef.current(state as T),
    );
  }, [bridge, key]);
}

/**
 * Subscribes to realtime change events for a datastore collection
 * (ADR-012 Phase M4). Notify-then-refetch: the platform pushes a minimal
 * "this collection changed" invalidation and `onInvalidate` decides what to
 * refetch — typically re-running the `useFiasDataStore().query(...)` your
 * component renders from.
 *
 * Requires the `data:store` permission (same as reading the collection —
 * a live invalidation grants nothing polling couldn't retrieve).
 *
 * Semantics and guarantees:
 * - Best-effort, at-most-once delivery: events can be lost or duplicated;
 *   the initial refetch fires AFTER the subscription is live, so a write
 *   racing the subscribe is never missed.
 * - `onInvalidate` runs single-flight with one trailing call: a burst of
 *   events during an in-flight refetch coalesces into exactly one more.
 * - The host renews authorization periodically; when it definitively ends
 *   (`status: 'ended'`), `endedReason` says why — `collection_deleted` and
 *   `authorization_revoked` are terminal, others may warrant resubscribing
 *   (unmount/remount or toggle `enabled`).
 * - Scope-aware: shared collections notify on any user's writes; user-scoped
 *   collections only on yours; workspace-scoped need `workspaceId` and an
 *   active membership.
 *
 * @example
 * const dataStore = useFiasDataStore();
 * const [scores, setScores] = useState<DataStoreDocument[]>([]);
 * const refresh = useCallback(async () => {
 *   const res = await dataStore.query('scores', { limit: 50 });
 *   setScores(res.documents);
 * }, [dataStore]);
 * const { status } = useDataSubscription('scores', refresh);
 */
export function useDataSubscription(
  collection: string,
  onInvalidate: () => void | Promise<void>,
  options?: { workspaceId?: string; enabled?: boolean },
): DataSubscriptionState {
  const bridge = useBridge();
  const [state, setState] = useState<DataSubscriptionState>({ status: 'subscribing' });
  const onInvalidateRef = useRef(onInvalidate);
  onInvalidateRef.current = onInvalidate;
  const enabled = options?.enabled ?? true;
  const workspaceId = options?.workspaceId;

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'ended', endedReason: 'unsubscribed' });
      return;
    }
    let cancelled = false;
    let subscriptionId: string | null = null;
    let unsubscribeEvent: (() => void) | null = null;
    let unsubscribeEnded: (() => void) | null = null;

    // Single-flight + exactly one trailing call: events arriving during an
    // in-flight refetch coalesce into one follow-up, never a parallel run.
    let refetching = false;
    let trailing = false;
    const runInvalidate = async (): Promise<void> => {
      if (refetching) {
        trailing = true;
        return;
      }
      refetching = true;
      try {
        await onInvalidateRef.current();
      } catch {
        // The caller's refetch failed — their concern; the subscription
        // stays live and the next event tries again.
      }
      refetching = false;
      if (trailing && !cancelled) {
        trailing = false;
        void runInvalidate();
      }
    };

    setState({ status: 'subscribing' });
    void (async () => {
      try {
        const res = await bridge.request<{
          output?: { kind?: string; subscriptionId?: string };
        }>('entity_invoke', {
          entityId: PLUGIN_DATASTORE_ENTITY_ID,
          payload: { op: 'subscribe', collection, workspaceId },
        });
        const subscription = res?.output;
        if (
          !subscription ||
          subscription.kind !== 'subscription' ||
          typeof subscription.subscriptionId !== 'string'
        ) {
          if (!cancelled) setState({ status: 'ended', endedReason: 'transport_error' });
          return;
        }
        if (cancelled) {
          bridge.releaseSubscription(subscription.subscriptionId);
          return;
        }
        subscriptionId = subscription.subscriptionId;
        unsubscribeEvent = bridge.onSubscriptionEvent(subscriptionId, () => void runInvalidate());
        unsubscribeEnded = bridge.onSubscriptionEnded(subscriptionId, (reason) => {
          if (!cancelled) setState({ status: 'ended', endedReason: reason });
        });
        setState({ status: 'active' });
        // Subscribe-then-fetch ordering: refetch AFTER the subscription is
        // live so a write racing the handshake is reflected either in this
        // fetch or in the event it triggers — never silently missed.
        void runInvalidate();
      } catch {
        if (!cancelled) setState({ status: 'ended', endedReason: 'transport_error' });
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeEvent?.();
      unsubscribeEnded?.();
      if (subscriptionId) bridge.releaseSubscription(subscriptionId);
    };
  }, [bridge, collection, workspaceId, enabled]);

  return state;
}

// ============================================================================
// useVaultUserDocuments — CONSENTED access to the user's pre-existing docs
// ============================================================================

/** Metadata of a user document this arche holds a grant for. */
export interface VaultUserDocumentSummary {
  documentId: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  sensitivity: string;
  createdAt: string;
  updatedAt: string;
  /**
   * What the user lets you do with this document: `'read'` (open it) or
   * `'write'` (also save changes to it — see `saveContent`). Open a `'read'`
   * document read-only, and call `requestEditAccess` when the user starts
   * editing. Optional: absent on hosts that predate edit access — treat
   * absent as `'read'`.
   */
  access?: 'read' | 'write';
  /**
   * Opaque token naming the document's current CONTENT. REQUIRED by
   * `saveContent` as `expectedRevision` — it is the user's file and they may
   * be changing it elsewhere. Never parse it.
   */
  revision?: string;
}

/** Result of the host picker: what the user picked and confirmed, or a
 * cancel. A cancel is a normal outcome — never treat it as an error. */
export type VaultUserDocumentPickResult =
  | { canceled: true }
  | {
      documents: Array<
        Pick<VaultUserDocumentSummary, 'documentId' | 'name' | 'mimeType' | 'sizeBytes'> & {
          /** What the user granted for this document. */
          access?: 'read' | 'write';
        }
      >;
    };

/** Input to `useVaultUserDocuments().saveContent`. */
export interface VaultUserDocumentSaveContentParams {
  documentId: string;
  /** The new content, up to 15 MB: a `string` (UTF-8 text — the document must
   *  be a text type) or bytes. Transport is chosen for you. The document's
   *  TYPE cannot change, so there is no `contentType`. */
  content: string | Uint8Array | ArrayBuffer | Blob;
  /** REQUIRED. The `revision` you last saw for this document. */
  expectedRevision: string;
}

export interface VaultUserDocumentsApi {
  /**
   * Open the HOST's Vault picker + consent sheet. The user hand-picks
   * documents and explicitly confirms the grant; your plugin never sees
   * their vault listing — only the picked documents come back. Must be
   * called from a user gesture (a button click), and only works in the
   * published plugin (the preview always resolves `{ canceled: true }`).
   */
  pick(options?: {
    maxDocuments?: number;
    /** Ask for EDIT access: the user is shown a consent that says your app
     *  may CHANGE what they pick. Needs `vault:user-documents:edit` IN
     *  ADDITION TO `vault:user-documents:read` — `:edit` does not include
     *  `:read`, and every method of this hook (this one included) is gated
     *  on `:read`. */
    access?: 'read' | 'write';
    /**
     * The types you can open: MIME types (`'application/pdf'`), MIME
     * families (`'image/*'`) or extensions (`'.docx'`), up to 20. The host
     * offers matching files; the user can still reveal the rest, shown
     * disabled. Omitted: every type.
     */
    accept?: readonly string[];
  }): Promise<VaultUserDocumentPickResult>;
  /**
   * Ask the user to let you EDIT a document you can already open. The host
   * shows its own consent for that one file — no picker — and resolves with
   * the document (now `access: 'write'`), or `{ canceled: true }`. Call it
   * from a user gesture, when the user actually starts editing — not on open,
   * and not again right after a decline. Needs `vault:user-documents:edit`
   * together with `vault:user-documents:read`.
   * If you hold no grant on the document the host shows the ordinary picker
   * instead, so you learn nothing about files you were not given.
   *
   * CHECK THE RESULT — a resolved promise is not "you may now edit
   * `documentId`". Because of that picker fallback the one document that
   * comes back may be a DIFFERENT one the user chose, and the user may have
   * answered from a sheet that offered view-only. Proceed only when
   * `documents[0].documentId === documentId` and `documents[0].access ===
   * 'write'`; treat anything else (including `{ canceled: true }`) as "no".
   */
  requestEditAccess(documentId: string): Promise<VaultUserDocumentPickResult>;
  /**
   * Save changes, IN PLACE, to a document the user has given you EDIT access
   * to — same `documentId`, so nothing attached to it detaches. Text or bytes,
   * up to 15 MB; one method, transport chosen for you.
   *
   * Manifest: `vault:user-documents:edit` AND `vault:user-documents:read`.
   * Declaring `:edit` alone is not enough — it does not include `:read`.
   *
   * It is the USER'S file, which makes this stricter than saving your own:
   * `expectedRevision` is REQUIRED (they may be editing it elsewhere — a stale
   * one rejects `VERSION_CONFLICT`: re-read, merge or ask, retry); the file's
   * TYPE cannot change (`MIME_CHANGE_NOT_ALLOWED`); and the platform keeps
   * earlier versions for them, which count against THEIR storage — a save
   * that would exceed it rejects `STORAGE_QUOTA_EXCEEDED` and nothing is lost.
   *
   * Rejects `EDIT_ACCESS_REQUIRED` when you hold view-only access (call
   * `requestEditAccess`), and `DOCUMENT_NOT_FOUND` once the user removes your
   * access in Vault → Arche Access — which takes effect on your very next save.
   * Handle both. Debounce (a few seconds idle, plus blur and close) and
   * surface `RATE_LIMIT` rather than looping: this lane allows fewer saves per
   * minute than your own documents do. Every save is recorded with your app's
   * name, and the file shows the user that you changed it.
   */
  saveContent(
    params: VaultUserDocumentSaveContentParams,
  ): Promise<VaultUserDocumentSaveContentResult>;
  /** Documents the user has granted this arche (empty until they pick). */
  list(options?: { limit?: number; offset?: number }): Promise<VaultUserDocumentSummary[]>;
  /**
   * Read one granted document. `includeContent: true` returns UTF-8 text
   * for text documents (a binary document rejects with BINARY_DOCUMENT —
   * use getDownloadUrl for those). Rejects with DOCUMENT_NOT_FOUND once
   * the user revokes access in Vault → Arche Access — handle that path.
   */
  get(
    documentId: string,
    options?: { includeContent?: boolean },
  ): Promise<{ document: VaultUserDocumentSummary; content: string | null }>;
  /** 1-hour signed URL for a granted document's bytes (images, PDFs, …).
   *  Usable as an `<img src>`; NOT fetchable from the plugin iframe
   *  (`connect-src 'none'`) — to process binary content, use getBytes. */
  getDownloadUrl(
    documentId: string,
  ): Promise<{ url: string; expiresAt: number; contentType: string | null }>;
  /**
   * Read a granted document's raw bytes (any content type) — the path for
   * binary-processing plugins (PDF editors, image tools). Size-capped
   * server-side (15 MB; larger documents reject with
   * CONSENTED_DOCUMENT_TOO_LARGE naming the cap — ask the user to open the
   * file manually instead).
   * This verb carries its own per-minute budget, tighter than the rest of
   * the family, because it is the only one that can move megabytes: call
   * sequentially and surface a RATE_LIMIT error to the user/assistant
   * verbatim rather than retrying in a loop. Rejects with
   * DOCUMENT_NOT_FOUND once the user revokes access in Vault → Arche Access
   * — unlike a presigned URL from getDownloadUrl, which stays valid for
   * its TTL, every getBytes call re-checks the grant.
   */
  getBytes(
    documentId: string,
  ): Promise<{ document: VaultUserDocumentSummary; bytes: Uint8Array; contentType: string | null }>;
  /**
   * WORKSPACE-APPROVED reads. When the user is acting in an org workspace
   * whose admin (or its Company) has approved your arche, these read that
   * workspace's documents with no per-document pick — every document the
   * acting member can see, at their role, never proprietary. Same
   * permission (`vault:user-documents:read`), same shapes as the methods
   * above. Two refusals to handle: `WORKSPACE_REQUIRED` (the user is in
   * their Personal vault — offer `pick()`) and `ARCHE_NOT_APPROVED` (this
   * workspace has not approved your arche — say so; approval is an admin's
   * act in Workspace Controls, and revocation is immediate).
   */
  workspace: {
    list(options?: {
      limit?: number;
      offset?: number;
      search?: string;
    }): Promise<VaultUserDocumentSummary[]>;
    get(
      documentId: string,
      options?: { includeContent?: boolean },
    ): Promise<{ document: VaultUserDocumentSummary; content: string | null }>;
    getDownloadUrl(
      documentId: string,
    ): Promise<{ url: string; expiresAt: number; contentType: string | null }>;
    getBytes(documentId: string): Promise<{
      document: VaultUserDocumentSummary;
      bytes: Uint8Array;
      contentType: string | null;
    }>;
  };
}

/**
 * Consented access to documents the USER already owns — the only SDK
 * surface that reaches data your plugin did not create. Requires the
 * `vault:user-documents:read` permission. Every access is user-consented
 * (per-document grants created in the host picker, revocable any time in
 * Vault → Arche Access) and audited by the platform.
 *
 * The picker offers the files of the workspace the user is acting in (the
 * host's workspace switcher — Personal, or an org workspace they belong to)
 * and `list()` returns the grants made in it; a document you hold a grant on
 * stays readable by id whichever workspace the user is in, because a grant
 * belongs to the workspace the document lives in. A plugin never names a
 * workspace; the host does. A member's role caps what they can share and
 * read. Proprietary-sensitivity
 * documents are never grantable. For documents your plugin CREATES, use
 * `useVaultDocuments` instead.
 */
export function useVaultUserDocuments(): VaultUserDocumentsApi {
  const bridge = useBridge();

  const pick = useCallback(
    async (options?: {
      maxDocuments?: number;
      access?: 'read' | 'write';
      accept?: readonly string[];
    }): Promise<VaultUserDocumentPickResult> => {
      return bridge.request<VaultUserDocumentPickResult>('vault_documents_pick', {
        maxDocuments: options?.maxDocuments,
        access: options?.access,
        ...(options?.accept ? { accept: [...options.accept] } : {}),
      });
    },
    [bridge],
  );

  const requestEditAccess = useCallback(
    async (documentId: string): Promise<VaultUserDocumentPickResult> => {
      // The same host-mediated op as `pick`, naming one document. The HOST
      // decides whether it may skip the picker (only for a document this arche
      // already holds a grant on) — nothing here can.
      return bridge.request<VaultUserDocumentPickResult>('vault_documents_pick', {
        maxDocuments: 1,
        access: 'write',
        documentId,
      });
    },
    [bridge],
  );

  const saveContent = useCallback(
    async (
      params: VaultUserDocumentSaveContentParams,
    ): Promise<VaultUserDocumentSaveContentResult> => {
      const routed = await routeSaveContent(params.content);
      if (routed.lane === 'body') {
        return invokeEntityOp<VaultUserDocumentSaveContentResult>(
          bridge,
          VAULT_USER_DOCUMENTS_ENTITY_ID,
          'save_consented_document_content',
          {
            documentId: params.documentId,
            content: routed.text,
            expectedRevision: params.expectedRevision,
          },
        );
      }
      // Bytes, or text past the bridge body: the host does the presigned PUT
      // (the iframe's CSP forbids it). `saveTarget: 'user'` selects the
      // consented lane; no `name`, so an older host refuses rather than
      // creating a new document.
      return bridge.request<VaultUserDocumentSaveContentResult>('vault_documents_upload', {
        saveToDocumentId: params.documentId,
        saveTarget: 'user',
        bytes: bytesToBase64(routed.bytes),
        contentKind: routed.contentKind,
        expectedRevision: params.expectedRevision,
      });
    },
    [bridge],
  );

  const list = useCallback(
    async (options?: { limit?: number; offset?: number }): Promise<VaultUserDocumentSummary[]> => {
      const res = await invokeEntityOp<{ documents: VaultUserDocumentSummary[] }>(
        bridge,
        VAULT_USER_DOCUMENTS_ENTITY_ID,
        'list_consented_documents',
        { limit: options?.limit, offset: options?.offset },
      );
      return res.documents;
    },
    [bridge],
  );

  const get = useCallback(
    async (
      documentId: string,
      options?: { includeContent?: boolean },
    ): Promise<{ document: VaultUserDocumentSummary; content: string | null }> => {
      return invokeEntityOp<{ document: VaultUserDocumentSummary; content: string | null }>(
        bridge,
        VAULT_USER_DOCUMENTS_ENTITY_ID,
        'get_consented_document',
        { documentId, includeContent: options?.includeContent },
      );
    },
    [bridge],
  );

  const getDownloadUrl = useCallback(
    async (
      documentId: string,
    ): Promise<{ url: string; expiresAt: number; contentType: string | null }> => {
      const res = await invokeEntityOp<{
        url: string;
        expiresAt: string;
        contentType: string | null;
      }>(bridge, VAULT_USER_DOCUMENTS_ENTITY_ID, 'get_consented_document_url', { documentId });
      return {
        url: res.url,
        expiresAt: new Date(res.expiresAt).getTime(),
        contentType: res.contentType,
      };
    },
    [bridge],
  );

  const getBytes = useCallback(
    async (
      documentId: string,
    ): Promise<{
      document: VaultUserDocumentSummary;
      bytes: Uint8Array;
      contentType: string | null;
    }> => {
      const res = await invokeEntityOp<{
        document: VaultUserDocumentSummary;
        bytesBase64: string;
        contentType: string | null;
      }>(bridge, VAULT_USER_DOCUMENTS_ENTITY_ID, 'get_consented_document_bytes', { documentId });
      return {
        document: res.document,
        bytes: base64ToBytes(res.bytesBase64),
        contentType: res.contentType,
      };
    },
    [bridge],
  );

  // Workspace-approved reads (ADR-022 §7): the same four shapes on the
  // acting workspace, admitted by the platform only under an approval.
  const workspace = useMemo(
    () => ({
      list: async (options?: { limit?: number; offset?: number; search?: string }) => {
        const res = await invokeEntityOp<{ documents: VaultUserDocumentSummary[] }>(
          bridge,
          VAULT_USER_DOCUMENTS_ENTITY_ID,
          'list_workspace_documents',
          { limit: options?.limit, offset: options?.offset, search: options?.search },
        );
        return res.documents;
      },
      get: (documentId: string, options?: { includeContent?: boolean }) =>
        invokeEntityOp<{ document: VaultUserDocumentSummary; content: string | null }>(
          bridge,
          VAULT_USER_DOCUMENTS_ENTITY_ID,
          'get_workspace_document',
          { documentId, includeContent: options?.includeContent },
        ),
      getDownloadUrl: async (documentId: string) => {
        const res = await invokeEntityOp<{
          url: string;
          expiresAt: string;
          contentType: string | null;
        }>(bridge, VAULT_USER_DOCUMENTS_ENTITY_ID, 'get_workspace_document_url', { documentId });
        return {
          url: res.url,
          expiresAt: new Date(res.expiresAt).getTime(),
          contentType: res.contentType,
        };
      },
      getBytes: async (documentId: string) => {
        const res = await invokeEntityOp<{
          document: VaultUserDocumentSummary;
          bytesBase64: string;
          contentType: string | null;
        }>(bridge, VAULT_USER_DOCUMENTS_ENTITY_ID, 'get_workspace_document_bytes', { documentId });
        return {
          document: res.document,
          bytes: base64ToBytes(res.bytesBase64),
          contentType: res.contentType,
        };
      },
    }),
    [bridge],
  );

  return useMemo(
    () => ({
      pick,
      requestEditAccess,
      saveContent,
      list,
      get,
      getDownloadUrl,
      getBytes,
      workspace,
    }),
    [pick, requestEditAccess, saveContent, list, get, getDownloadUrl, getBytes, workspace],
  );
}

/**
 * Refresh a community-asset URL this many ms before it expires. Larger than the
 * Vault buffer relative to the TTL (5 min vs 1 h) because a gallery render can
 * start well before its `<img>` loads finish.
 */
const COMMUNITY_ASSET_URL_REFRESH_BUFFER_MS = 30_000;

/**
 * Module-scoped cache of community-asset signed URLs, keyed by assetId.
 *
 * Same reasoning as the Vault download-URL cache above: a React render is not
 * the right invalidation boundary for something valid for a fixed wall-clock
 * window.
 *
 * Keyed by assetId ALONE, deliberately: this module is loaded inside one
 * plugin iframe, which runs one arche, so there is no second arche to collide
 * with. (The signature itself is still bound to the arche server-side — that
 * boundary does not depend on this cache.)
 *
 * Because signatures last only ~5 minutes, this cache is what stops a gallery
 * from re-minting on every render — while `unpublish` evicts eagerly so a
 * removed asset stops resolving straight away rather than at TTL.
 */
const communityAssetUrlCache = new Map<string, CommunityAssetUrl>();
const communityAssetUrlInflight = new Map<string, Promise<CommunityAssetUrl[]>>();

/**
 * Batch resolver behind `getUrl` / `getUrls`.
 *
 * Serves what it can from cache, requests only the misses, and dedupes
 * concurrent requests for the same asset so a burst of mounting `<img>`
 * components collapses into one round-trip. Ids the server omits (taken down,
 * not yet published, never existed) are simply absent from the result.
 */
async function fetchCommunityAssetUrls(
  bridge: FiasBridge,
  assetIds: string[],
): Promise<CommunityAssetUrl[]> {
  const now = Date.now();
  const hits: CommunityAssetUrl[] = [];
  const misses: string[] = [];

  for (const assetId of assetIds) {
    const cached = communityAssetUrlCache.get(assetId);
    if (cached && now < cached.expiresAt - COMMUNITY_ASSET_URL_REFRESH_BUFFER_MS) {
      hits.push(cached);
    } else {
      misses.push(assetId);
    }
  }
  if (misses.length === 0) return hits;

  // Coalesce with any in-flight request covering the same asset.
  const pending: Array<Promise<CommunityAssetUrl[]>> = [];
  const toRequest: string[] = [];
  for (const assetId of misses) {
    const inflight = communityAssetUrlInflight.get(assetId);
    if (inflight) pending.push(inflight);
    else toRequest.push(assetId);
  }

  // CHUNKED to the server's cap. `get_urls` is `.max(COMMUNITY_ASSET_MAX_URL_BATCH)`
  // server-side and Zod rejects the whole request when it is exceeded — so an
  // un-chunked gallery of 60 images did not render 50 and drop 10, it rendered
  // NONE. Chunking here rather than surfacing the limit keeps the natural usage
  // (one book, every page at once) working, which is the case this hook exists
  // for. Chunks run concurrently; each caches and clears its own ids.
  for (let i = 0; i < toRequest.length; i += COMMUNITY_ASSET_MAX_URL_BATCH) {
    const chunk = toRequest.slice(i, i + COMMUNITY_ASSET_MAX_URL_BATCH);
    const promise = invokeEntityOp<{ urls: CommunityAssetUrl[] }>(
      bridge,
      COMMUNITY_ASSETS_ENTITY_ID,
      'get_urls',
      { assetIds: chunk },
    )
      .then((res) => {
        for (const url of res.urls) {
          communityAssetUrlCache.set(url.assetId, url);
        }
        return res.urls;
      })
      .finally(() => {
        for (const assetId of chunk) {
          communityAssetUrlInflight.delete(assetId);
        }
      });
    for (const assetId of chunk) {
      communityAssetUrlInflight.set(assetId, promise);
    }
    pending.push(promise);
  }

  const settled = await Promise.all(pending);
  const requested = new Set(assetIds);
  const seen = new Set(hits.map((h) => h.assetId));
  for (const batch of settled) {
    for (const url of batch) {
      // A coalesced in-flight request may cover assets we weren't asked about.
      if (!requested.has(url.assetId) || seen.has(url.assetId)) continue;
      seen.add(url.assetId);
      hits.push(url);
    }
  }
  return hits;
}

/**
 * Send email AS the Company this app is being used in (ADR-023), from the
 * Company's own verified domain — not from Fias.
 *
 * Requires `email:company:send` in `fias-plugin.json`, AND a Company admin
 * authorizing this app to send (they choose who it may email: this workspace,
 * the whole Company, or anyone). The user must be working in one of the
 * Company's workspaces with edit access; each recipient is charged to them.
 * The From address is always the Company's — you only choose recipients and
 * content.
 *
 * Name members by `userId`; you never see their addresses. Every email carries
 * a one-click unsubscribe, and people who used it are skipped (`muted`).
 *
 * @example
 * ```tsx
 * const email = useCompanyEmail();
 * const result = await email.send({
 *   requestKey: `digest-${weekId}`,
 *   to: members.map((m) => ({ userId: m.userId })),
 *   subject: 'Your weekly digest',
 *   text: digestText,
 *   html: digestHtml,
 * });
 * ```
 */
export function useCompanyEmail(): CompanyEmailApi {
  const bridge = useBridge();
  const send = useCallback(
    async (params: CompanyEmailSendParams): Promise<CompanyEmailSendResult> => {
      // Single-verb entity: the payload carries no `op` (see entity-ops.ts).
      const res = await bridge.request<{ output: CompanyEmailSendResult }>('entity_invoke', {
        entityId: COMPANY_EMAIL_SEND_ENTITY_ID,
        payload: params,
      });
      return res.output;
    },
    [bridge],
  );
  return useMemo(() => ({ send }), [send]);
}

/**
 * Publish images to this arche's community, and browse what others published.
 *
 * The platform's only cross-user read path: an image published here is visible
 * to every authenticated user of THIS arche (and no other), after it clears
 * moderation. Publishing is free.
 *
 * Two things to design around:
 *
 *   1. **Publishing is not instant.** `publish` returns
 *      `status: 'pending_moderation'`; the asset appears in `listCommunity`
 *      only once it is approved. Show the author their own pending item via
 *      `listMine` rather than optimistically adding it to the community feed.
 *   2. **Store structure in the Data Store, not here.** Which asset belongs on
 *      which page/level goes in a `shared`-scope collection holding `assetId`s.
 *      Community Assets holds the bytes and nothing queryable.
 *
 * Permissions: `assets:community:read` for browsing and viewing;
 * `assets:community:publish` additionally for publish / unpublish / listMine.
 * A plugin that only DISPLAYS community content should declare only the former.
 *
 * @returns Community assets API.
 */
export function useCommunityAssets(): CommunityAssetsApi {
  const bridge = useBridge();

  const publish = useCallback(
    async (input: CommunityAssetPublishInput): Promise<CommunityAsset> => {
      const res = await invokeEntityOp<{ asset: CommunityAsset }>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'publish',
        {
          fileId: input.fileId,
          title: input.title,
          caption: input.caption,
          tags: input.tags,
          clientRef: input.clientRef,
          attributionName: input.attributionName,
        },
      );
      return res.asset;
    },
    [bridge],
  );

  const publishMany = useCallback(
    async (items: CommunityAssetPublishInput[]): Promise<CommunityAssetPublishResult[]> => {
      // Chunked to the server's cap for the same reason as `getUrls` — over the
      // limit, Zod rejects the entire call, so "publish this 30-page book"
      // published nothing rather than the first 20.
      //
      // SEQUENTIAL, unlike the URL chunks: each publish normalizes an image,
      // writes a row and stages bytes to S3, and firing several chunks at once
      // would push a burst through the per-(user, arche) quota checks and turn
      // a partial success into a wall of QUOTA_EXCEEDED entries. Items already
      // fail independently server-side, so results stay per-item either way.
      const results: CommunityAssetPublishResult[] = [];
      for (let i = 0; i < items.length; i += COMMUNITY_ASSET_MAX_PUBLISH_BATCH) {
        const res = await invokeEntityOp<{ results: CommunityAssetPublishResult[] }>(
          bridge,
          COMMUNITY_ASSETS_ENTITY_ID,
          'publish_many',
          { items: items.slice(i, i + COMMUNITY_ASSET_MAX_PUBLISH_BATCH) },
        );
        results.push(...res.results);
      }
      return results;
    },
    [bridge],
  );

  const unpublish = useCallback(
    async (assetId: string): Promise<CommunityAsset> => {
      const res = await invokeEntityOp<{ asset: CommunityAsset }>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'unpublish',
        { assetId },
      );
      // Evict eagerly so a removed asset stops resolving from cache now, rather
      // than up to the signature TTL later.
      communityAssetUrlCache.delete(assetId);
      return res.asset;
    },
    [bridge],
  );

  const listMine = useCallback(
    async (options?: CommunityAssetListOptions): Promise<CommunityAssetListResult> =>
      invokeEntityOp<CommunityAssetListResult>(bridge, COMMUNITY_ASSETS_ENTITY_ID, 'list_mine', {
        limit: options?.limit,
        cursor: options?.cursor,
      }),
    [bridge],
  );

  const listCommunity = useCallback(
    async (options?: CommunityAssetListOptions): Promise<CommunityAssetListResult> =>
      invokeEntityOp<CommunityAssetListResult>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'list_community',
        { limit: options?.limit, cursor: options?.cursor, tag: options?.tag },
      ),
    [bridge],
  );

  const get = useCallback(
    async (assetId: string): Promise<CommunityAsset> => {
      const res = await invokeEntityOp<{ asset: CommunityAsset }>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'get',
        { assetId },
      );
      return res.asset;
    },
    [bridge],
  );

  const getUrls = useCallback(
    (assetIds: string[]): Promise<CommunityAssetUrl[]> => fetchCommunityAssetUrls(bridge, assetIds),
    [bridge],
  );

  const getUrl = useCallback(
    async (assetId: string): Promise<CommunityAssetUrl> => {
      const [url] = await fetchCommunityAssetUrls(bridge, [assetId]);
      if (!url) {
        throw new Error(`Community asset ${assetId} is not available`);
      }
      return url;
    },
    [bridge],
  );

  const report = useCallback(
    async (
      assetId: string,
      reason: CommunityAssetReportReason,
      details?: string,
    ): Promise<{ reportId: string; status: string }> =>
      invokeEntityOp<{ reportId: string; status: string }>(
        bridge,
        COMMUNITY_ASSETS_ENTITY_ID,
        'report',
        { assetId, reason, details },
      ),
    [bridge],
  );

  return useMemo(
    () => ({
      publish,
      publishMany,
      unpublish,
      listMine,
      listCommunity,
      get,
      getUrl,
      getUrls,
      report,
    }),
    [publish, publishMany, unpublish, listMine, listCommunity, get, getUrl, getUrls, report],
  );
}

// =============================================================================
// Fias AI actions (ui_action client plane)
// =============================================================================

/** Configuration for {@link useFiasAIActions}. */
export interface FiasAIActionsConfig extends FiasAIActionsDeclaration {
  /**
   * The state snapshot the assistant sees, and `availableWhen` filters
   * against. Keep it SMALL and flag-shaped (`{documentOpen: true}` — publish
   * `hasPages: true`, not a page array): accepted snapshots are embedded in
   * the assistant's system prompt every turn, and the host drops oversized
   * ones outright.
   */
  state?: Record<string, unknown>;
  /**
   * Perform a dispatched action. Throw/reject to report failure — the
   * assistant AWAITS this before telling the model the action applied, so a
   * batch ("do X to each file") stops on the failing step instead of
   * marching on. ALWAYS re-check preconditions here: `availableWhen`
   * filtering is presentation, and a stale turn can dispatch a hidden verb.
   */
  onAction: (actionId: string, params: Record<string, unknown>) => void | Promise<unknown>;
}

/**
 * Receive a document the platform hands your app to open (Arche Handoff).
 *
 * Fires when the user says "open this in <your app>" — from the Fias AI
 * assistant, from an email attachment, from any "open in" affordance on a
 * Vault document. Your handler runs once per handoff; a handoff that arrives
 * before this hook mounts is buffered and delivered on registration, so the
 * first one of a session is not lost.
 *
 * ```tsx
 * useArcheHandoff((payload) => {
 *   if (payload.kind !== 'open_document') return;
 *   void openDocumentAsUnsavedCopy(payload.documentId);
 * });
 * ```
 *
 * You get a document REFERENCE, not bytes. Read it with
 * `useVaultUserDocuments().getBytes(documentId)`: the host grants you that one
 * document as it relays the handoff, so no `pick()` is needed first. The grant
 * is per-document and revocable in Vault → Arche Access exactly like a picked
 * one, and it needs `vault:user-documents:read` — declare it, or the grant is
 * refused and the file never arrives.
 *
 * Open it as an UNSAVED COPY: a later save must create a new document, never
 * mutate the source. And you are the authoritative type gate — if you cannot
 * open this file, tell the user plainly rather than opening something broken.
 *
 * To be offered as a destination at all, your arche must be registered for
 * document intake on the platform side; declaring this hook alone does not
 * enrol you.
 */
export function useArcheHandoff(onHandoff: (payload: ArcheHandoffPayload) => void): void {
  const bridge = useBridge();
  // Read through a ref so a new closure each render doesn't churn the
  // registration (and re-drain the buffered handoff).
  const handlerRef = useRef(onHandoff);
  handlerRef.current = onHandoff;

  useEffect(() => {
    const handler = (payload: ArcheHandoffPayload) => handlerRef.current(payload);
    bridge.setHandoffHandler(handler);
    return () => bridge.clearHandoffHandler(handler);
  }, [bridge]);
}

// ---------------------------------------------------------------------------
// Document Sign — reading back an envelope your app started
// ---------------------------------------------------------------------------

/** One recipient of an envelope your app started.
 *
 *  No email address, deliberately. A recipient's contact details are a third
 *  party's, and name + role + state is what an approval-status UI needs. If
 *  your app supplied the recipients, you already have their addresses. */
export interface SignEnvelopeRecipient {
  recipientId: string;
  name: string | null;
  role: 'signer' | 'viewer' | 'approver';
  status:
    | 'pending'
    | 'notified'
    | 'viewed'
    | 'signed'
    | 'declined'
    | 'declined_electronic'
    | 'expired';
  /** Position in a sequential signing order; 0-based. Meaningless for parallel. */
  routingOrder: number;
  signedAt: string | null;
  declinedAt: string | null;
}

/** An envelope your app started, as your app may see it. Never its documents. */
export interface SignEnvelope {
  envelopeId: string;
  title: string;
  status: 'draft' | 'sent' | 'in_progress' | 'completed' | 'declined' | 'voided' | 'expired';
  signingOrder: 'parallel' | 'sequential';
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  voidedAt: string | null;
  declinedAt: string | null;
  expiresAt: string | null;
  recipients: SignEnvelopeRecipient[];
}

/** One entry of an envelope's audit timeline. A projection, not the stored
 *  row: signer IP addresses, user agents and the hash-chain columns stay
 *  server-side. */
export interface SignEnvelopeEvent {
  /** e.g. `EnvelopeSent`, `RecipientViewed`, `RecipientSigned`, `EnvelopeCompleted`. */
  type: string;
  actorKind: 'owner' | 'recipient' | 'system';
  /** Which recipient the event is about, when it is about one. */
  recipientId: string | null;
  createdAt: string;
}

export interface SignEnvelopeEventsPage {
  events: SignEnvelopeEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface DocumentSignApi {
  /**
   * Read the current state of one envelope your app started — status, who the
   * recipients are, and where each of them has got to.
   *
   * Rejects with `ENVELOPE_NOT_FOUND_OR_NOT_GRANTED` for an envelope your app
   * did not start, one on a workspace the user cannot act for, one that does
   * not exist, one that is still a DRAFT, AND one whose access the user has
   * since removed. Those are deliberately the same error: a distinguishable
   * "it exists but you cannot see it" would let anyone probe for envelope
   * ids. Handle it as "we can no longer follow this", not as a bug.
   *
   * A draft is not readable until the user presses Send — that is the moment
   * they grant access. A `saved_draft` handoff result gives you an id to keep;
   * reading it succeeds once the envelope is sent, not before.
   */
  getEnvelope(envelopeId: string): Promise<SignEnvelope>;
  /**
   * Read the envelope's audit timeline, oldest first. Same access rules and
   * the same single error as `getEnvelope` — authorization is re-checked per
   * call, so a revocation between two calls stops the second one.
   */
  listEvents(
    envelopeId: string,
    options?: { limit?: number; offset?: number },
  ): Promise<SignEnvelopeEventsPage>;
}

/**
 * Follow the envelopes your app handed to Document Sign.
 *
 * Requires the `docsign:envelopes:read` permission, and reaches ONLY envelopes
 * your app itself started via a `sign_document` handoff — never one the user
 * created in Document Sign directly, never another app's, and never any
 * envelope's document bytes. Two independent gates, both of which the user
 * controls: they grant access by pressing Send on an envelope you handed over,
 * and they remove it in Vault → Arche Access.
 *
 * There is no list verb. Store the `envelopeId` from the
 * `sign_document_result` handoff against your own record and read by id —
 * which is also why that result must be matched to an outstanding `requestId`
 * and consumed once.
 *
 * **This is where truth comes from.** The handoff result is a POINTER: it
 * rides navigation state, which is same-origin forgeable, so it tells you
 * which envelope to look at and nothing more. Write "signed" into an approval
 * trail only on the strength of a read.
 *
 * There are no webhooks. Poll when your UI needs to know — on mount, on a
 * refresh the user asked for — not on a timer.
 *
 * ```tsx
 * const sign = useDocumentSign();
 * const envelope = await sign.getEnvelope(approval.envelopeId);
 * if (envelope.status === 'completed') markApproved(approval.id);
 * ```
 */
export function useDocumentSign(): DocumentSignApi {
  const bridge = useBridge();

  const getEnvelope = useCallback(
    async (envelopeId: string): Promise<SignEnvelope> => {
      const res = await invokeEntityOp<{ envelope: SignEnvelope }>(
        bridge,
        DOCUMENT_SIGN_ENTITY_ID,
        'get_consented_envelope',
        { envelopeId },
      );
      return res.envelope;
    },
    [bridge],
  );

  const listEvents = useCallback(
    async (
      envelopeId: string,
      options?: { limit?: number; offset?: number },
    ): Promise<SignEnvelopeEventsPage> => {
      return invokeEntityOp<SignEnvelopeEventsPage>(
        bridge,
        DOCUMENT_SIGN_ENTITY_ID,
        'list_consented_envelope_events',
        { envelopeId, limit: options?.limit, offset: options?.offset },
      );
    },
    [bridge],
  );

  return useMemo(() => ({ getEnvelope, listEvents }), [getEnvelope, listEvents]);
}

/**
 * Declare Fias AI actions at RUNTIME — the dynamic half of the plugin
 * AI-action plane (`ai:actions` permission; TRUSTED arches only — the host
 * silently ignores runtime declarations from non-trusted arches, whose lane
 * is the manifest `fiasAI` block, fixed at publish and reviewed with the
 * submission). Both lanes merge host-side; the manifest wins id collisions.
 *
 * Pass `null` to declare nothing (e.g. while loading). The declaration and
 * the state snapshot are each re-sent only when their content changes; the
 * host additionally coalesces rapid re-sends trailing-edge (latest wins,
 * applied within ~1s / ~250ms for state) — nothing is lost, but drive
 * declaration changes off real capability changes, not render churn.
 *
 * Returns `{ openAssistant }` — a fire-and-forget request that the host open
 * the global Fias AI panel, for CONTEXTUAL affordances (an empty-state "Ask
 * Fias AI" button; the host also shows its own "Works with Fias AI" chip).
 * The host debounces it and honours the user's last dismissal — never loop
 * or retry, and there is deliberately no way to pass text along.
 */
export function useFiasAIActions(config: FiasAIActionsConfig | null): {
  openAssistant: () => void;
} {
  const bridge = useBridge();

  // The handler is registered once per bridge and reads the latest onAction
  // through a ref — a re-render must not tear down dispatch handling.
  const onActionRef = useRef<FiasAIActionsConfig['onAction'] | null>(config?.onAction ?? null);
  onActionRef.current = config?.onAction ?? null;

  useEffect(() => {
    bridge.setAiActionHandler((actionId, params) => {
      const handler = onActionRef.current;
      if (!handler) {
        throw new Error('no Fias AI action handler is currently registered');
      }
      return handler(actionId, params);
    });
    return () => bridge.setAiActionHandler(null);
  }, [bridge]);

  // Declaration — content-diffed via JSON so callers may pass fresh object
  // literals each render without re-sending (the host throttles this lane).
  const declarationJson = config
    ? JSON.stringify({ description: config.description, actions: config.actions })
    : null;
  useEffect(() => {
    let cancelled = false;
    void bridge.waitForInit().then(() => {
      if (cancelled) return;
      bridge.declareAiActions(
        declarationJson ? (JSON.parse(declarationJson) as FiasAIActionsDeclaration) : null,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [bridge, declarationJson]);

  // Clear the declaration when the consumer unmounts — the assistant must
  // not keep offering verbs nothing will handle.
  useEffect(() => {
    return () => bridge.declareAiActions(null);
  }, [bridge]);

  // State snapshot — content-diffed like the declaration.
  const stateJson = config?.state ? JSON.stringify(config.state) : null;
  useEffect(() => {
    if (stateJson === null) return;
    let cancelled = false;
    void bridge.waitForInit().then(() => {
      if (!cancelled) bridge.sendAiState(JSON.parse(stateJson) as Record<string, unknown>);
    });
    return () => {
      cancelled = true;
    };
  }, [bridge, stateJson]);

  const openAssistant = useCallback(() => bridge.openAssistant(), [bridge]);
  return useMemo(() => ({ openAssistant }), [openAssistant]);
}
